import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { alpha, styled } from '@mui/material/styles';
import { Search, User, Phone, WalletCards, Calculator, TrendingUp, BadgePercent, ThumbsUp, HelpCircle, AlertTriangle, X, Mail, Eye, Download, ChevronDown, ChevronRight } from 'lucide-react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LoaderProgress from '../components/ui/loader-progress';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useJob } from '../context/JobContext';
import { useAuth } from '../context/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

const StatWidget = ({ title, value, icon: Icon, color, subtitle }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={(theme) => ({
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.14) : '#e2e8f0',
      borderRadius: 2,
      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.78) : '#ffffff'
    })}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.15 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  </Paper>
);

const getTransazioneWorkflowProgress = (row) => {
  const hasPiano = Boolean(row?.last_piano_rateale_il);
  const hasSollecito = Boolean(row?.last_sollecito_il);
  const hasCalcolo = String(row?.stato_movimento || '') === 'verificato';

  const done = hasPiano ? 3 : hasSollecito ? 2 : hasCalcolo ? 1 : 0;
  const total = 3;
  const percent = total > 0 ? (done / total) * 100 : 0;
  return { done, total, percent };
};

const formatPercentCompactIt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const decimals = Math.abs(n - Math.round(n)) < 1e-9 ? 0 : 2;
  return `${n.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
};

const WorkflowProgressRing = ({ row }) => {
  const progress = getTransazioneWorkflowProgress(row);
  const pct = Math.max(0, Math.min(100, Number(progress.percent || 0)));
  const label = formatPercentCompactIt(pct);

  return (
    <Tooltip title={`Avanzamento: ${progress.done}/${progress.total} (${label})`}>
      <Box sx={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={44}
          thickness={5}
          sx={(theme) => ({
            color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.22 : 0.12)
          })}
        />
        <CircularProgress
          variant="determinate"
          value={pct}
          size={44}
          thickness={5}
          sx={(theme) => ({
            color: theme.palette.warning.main,
            position: 'absolute',
            left: 0,
            top: 0
          })}
        />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" sx={{ fontWeight: 800, lineHeight: 1 }}>
            {label}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const IncassatoPercentRing = ({ value }) => {
  const num = Number(value);
  const clamped = Number.isFinite(num) ? Math.max(0, Math.min(100, num)) : 0;
  const label = `${Math.round(clamped)}%`;
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" value={100} size={42} thickness={5} sx={{ color: 'grey.200' }} />
      <CircularProgress
        variant="determinate"
        value={clamped}
        size={42}
        thickness={5}
        sx={{
          position: 'absolute',
          left: 0,
          color: clamped >= 100 ? 'success.main' : 'warning.main'
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 800 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

const BASE_FILTERS = {
  search: '',
  responsabileLegale: [],
  responsabileContabile: [],
  controparte: [],
  tipologia: [],
  cluster: [],
  ultimaFatturaAge: '',
  incassatoPercentRange: '',
  status: ['closed'],
  modalitaChiusura: [],
  statoVerifica: [],
  importoAccordatoFilter: '',
  dataAssegnazioneFrom: '',
  dataAssegnazioneTo: '',
  dataChiusuraFrom: '',
  dataChiusuraTo: ''
};

const IMPORTO_ACCORDATO_FILTER_OPTIONS = [
  { value: 'eq0', label: '= 0' },
  { value: 'gt0', label: '> 0' },
  { value: 'lt0', label: '< 0' }
];

const ULTIMA_FATTURA_AGE_OPTIONS = [
  { value: 'fresh_0_30', label: '0–30 gg (Verde)' },
  { value: 'warn_31_60', label: '31–60 gg (Arancio)' },
  { value: 'crit_gt_60', label: '> 60 gg (Rosso)' },
  { value: 'none', label: 'Senza fattura' }
];

const INCASSATO_PERCENT_RANGE_OPTIONS = [
  { value: '0', label: '0%' },
  { value: '1-25', label: '1–25%' },
  { value: '26-50', label: '26–50%' },
  { value: '51-75', label: '51–75%' },
  { value: '76-99', label: '76–99%' },
  { value: '100', label: '100%' }
];

const loadDefaultFilters = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const today = `${year}-${month}-${day}`;
  const monthStart = `${year}-${month}-01`;
  return {
    ...BASE_FILTERS,
    dataChiusuraFrom: monthStart,
    dataChiusuraTo: today
  };
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatMoney = (value) => {
  const n = Number(value);
  if (value == null || value === '' || Number.isNaN(n)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(n);
};

const getUtcDay = (value) => {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
};

const getDaysSince = (value) => {
  const invoiceDay = getUtcDay(value);
  if (invoiceDay == null) return null;
  const todayDay = getUtcDay(new Date());
  if (todayDay == null) return null;
  return Math.floor((todayDay - invoiceDay) / 86400000);
};

const getInvoiceAgeBucket = (invoiceDate) => {
  const days = getDaysSince(invoiceDate);
  if (days == null) return 'none';
  if (days <= 30) return 'fresh_0_30';
  if (days <= 60) return 'warn_31_60';
  return 'crit_gt_60';
};

const getInvoiceAgeColor = (theme, invoiceDate) => {
  const bucket = getInvoiceAgeBucket(invoiceDate);
  if (bucket === 'fresh_0_30') return theme.palette.success.main;
  if (bucket === 'warn_31_60') return theme.palette.warning.main;
  if (bucket === 'crit_gt_60') return theme.palette.error.main;
  return theme.palette.text.disabled;
};

const round2 = (value) => Math.round(Number(value || 0) * 100) / 100;

const formatNumber2 = (value, fallback = '') => {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return round2(n).toFixed(2);
};

const formatPercent = (value, decimals = 2) => {
  const n = Number(value);
  if (value == null || value === '' || Number.isNaN(n)) return '-';
  return `${n.toFixed(decimals)}%`;
};

const formatPercentIt = (value, decimals = 2) => {
  const n = Number(value);
  if (value == null || value === '' || Number.isNaN(n)) return '-';
  return `${n.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
};

const parseItNumber = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const normalized = raw.replace(/\s+/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const parseItDate = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
};

const formatDateItFromDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('it-IT');
};

const formatNumber2It = (value, fallback = '') => {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const CALCOLO_IPOTESI = [
  { key: 'piano_rientro', label: 'Piano di rientro', baseLabel: 'Interessi risparmiati' },
  { key: 'saldo_stralcio', label: 'Saldo e stralcio', baseLabel: 'Risparmio' },
  { key: 'carta_revolving', label: 'Carta revolving', baseLabel: 'Risparmio' },
  { key: 'finanziamento_decaduto', label: 'Finanziamento decaduto', baseLabel: 'Risparmio' }
];

const getCalcoloIpotesiLabel = (ipotesiKey) => CALCOLO_IPOTESI.find((o) => o.key === ipotesiKey)?.label || ipotesiKey || '';

const normalizeCalcoloIpotesiKey = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const lowered = s.toLowerCase();
  const normalized = lowered.replace(/[\s-]+/g, '_');
  if (CALCOLO_IPOTESI.some((o) => o.key === normalized)) return normalized;
  const byLabel = CALCOLO_IPOTESI.find((o) => String(o.label || '').toLowerCase() === lowered);
  if (byLabel) return byLabel.key;
  return '';
};

const getCalcoloIpotesiHelp = (ipotesiKey) => {
  if (ipotesiKey === 'piano_rientro') {
    return (
      <Box sx={{ p: 0.5, maxWidth: 360 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
          Piano di rientro
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Base Compenso = Interessi risparmiati
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
          Interessi totali = Totale da restituire − Importo erogato
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
          Interessi pagati = (Interessi totali ÷ N. rate) × Rate pagate
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mb: 0.5 }}>
          Interessi risparmiati = Interessi totali − Interessi pagati
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 900 }}>
          Importo Transazione = Base Compenso × (Compenso % / 100)
        </Typography>
      </Box>
    );
  }

  if (ipotesiKey === 'saldo_stralcio') {
    return (
      <Box sx={{ p: 0.5, maxWidth: 360 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
          Saldo e stralcio
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Base Compenso = Risparmio
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mb: 0.5 }}>
          Risparmio = Importo dovuto − Importo accordato
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 900 }}>
          Importo Transazione = Base Compenso × (Compenso % / 100)
        </Typography>
      </Box>
    );
  }

  if (ipotesiKey === 'carta_revolving') {
    return (
      <Box sx={{ p: 0.5, maxWidth: 360 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
          Carta revolving
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Base Compenso = Risparmio
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mb: 0.5 }}>
          Risparmio = Interessi da estratto conto
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 900 }}>
          Importo Transazione = Base Compenso × (Compenso % / 100)
        </Typography>
      </Box>
    );
  }

  if (ipotesiKey === 'finanziamento_decaduto') {
    return (
      <Box sx={{ p: 0.5, maxWidth: 360 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
          Finanziamento decaduto
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Base Compenso = Risparmio
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mb: 0.5 }}>
          Risparmio = Richiesto a decadenza − Importo accordato
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 900 }}>
          Importo Transazione = Base Compenso × (Compenso % / 100)
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 900 }}>
        Importo Transazione = Base Compenso × (Compenso % / 100)
      </Typography>
    </Box>
  );
};

const parseFlexibleNumber = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const n1 = Number(raw);
  if (Number.isFinite(n1)) return n1;
  return parseItNumber(raw);
};

const round0 = (value) => Math.round(Number(value || 0));

const computeCalcoloIpotesi = (ipotesiKey, inputs = {}) => {
  const v = (k) => parseFlexibleNumber(inputs[k]);
  const n = (k) => (v(k) == null ? null : Number(v(k)));

  if (ipotesiKey === 'piano_rientro') {
    const importoErogato = n('importo_erogato') || 0;
    const totaleDaRestituire = n('totale_da_restituire') || 0;
    const numeroRate = round0(n('numero_rate') || 0);
    const ratePagate = round0(n('rate_pagate') || 0);

    const rata = numeroRate > 0 ? totaleDaRestituire / numeroRate : 0;
    const interessiTotali = totaleDaRestituire - importoErogato;
    const interessiPerRata = numeroRate > 0 ? interessiTotali / numeroRate : 0;
    const interessiGiaPagati = interessiPerRata * ratePagate;
    const rateResidue = numeroRate - ratePagate;
    const debitoResiduo = totaleDaRestituire - rata * ratePagate;
    const interessiRisparmiati = interessiTotali - interessiGiaPagati;

    return {
      baseLabel: 'Interessi risparmiati',
      baseValue: round2(interessiRisparmiati),
      outputs: {
        rata: round2(rata),
        interessi_totali: round2(interessiTotali),
        interessi_per_rata: round2(interessiPerRata),
        interessi_gia_pagati: round2(interessiGiaPagati),
        rate_residue: round0(rateResidue),
        debito_residuo: round2(debitoResiduo),
        interessi_risparmiati: round2(interessiRisparmiati)
      }
    };
  }

  if (ipotesiKey === 'saldo_stralcio') {
    const importoDovuto = n('importo_dovuto') || 0;
    const importoAccordato = n('importo_accordato') || 0;
    const numeroRate = round0(n('numero_rate') || 0);
    const risparmio = importoDovuto - importoAccordato;
    const sconto = importoDovuto > 0 ? (risparmio / importoDovuto) * 100 : 0;
    const rata = numeroRate > 0 ? importoAccordato / numeroRate : 0;

    return {
      baseLabel: 'Risparmio',
      baseValue: round2(risparmio),
      outputs: {
        sconto_percent: round2(sconto),
        rata: round2(rata),
        risparmio: round2(risparmio)
      }
    };
  }

  if (ipotesiKey === 'carta_revolving') {
    const saldoDebito = n('saldo_debito') || 0;
    const interessiEstratto = n('interessi_estratto_conto') || 0;
    const capitaleNetto = saldoDebito - interessiEstratto;
    const incidenzaInteressi = saldoDebito > 0 ? (interessiEstratto / saldoDebito) * 100 : 0;
    const risparmio = interessiEstratto;

    return {
      baseLabel: 'Risparmio',
      baseValue: round2(risparmio),
      outputs: {
        capitale_netto_interessi: round2(capitaleNetto),
        incidenza_interessi_percent: round2(incidenzaInteressi),
        risparmio: round2(risparmio)
      }
    };
  }

  if (ipotesiKey === 'finanziamento_decaduto') {
    const importoErogato = n('importo_erogato') || 0;
    const totaleDaRestituire = n('totale_da_restituire') || 0;
    const numeroRate = round0(n('numero_rate') || 0);
    const importoPagato = n('importo_pagato') || 0;
    const importoScaduto = n('importo_scaduto') || 0;
    const richiestoDecadenza = n('richiesto_a_decadenza') || 0;
    const importoAccordato = n('importo_accordato') || 0;

    const rata = numeroRate > 0 ? totaleDaRestituire / numeroRate : 0;
    const ratePagate = rata > 0 ? importoPagato / rata : 0;
    const rateScaduteNonPagate = rata > 0 ? importoScaduto / rata : 0;
    const debitoResiduoPiano = totaleDaRestituire - importoPagato;
    const interessiTotali = totaleDaRestituire - importoErogato;
    const interessiPerRata = numeroRate > 0 ? interessiTotali / numeroRate : 0;
    const rateFuture = numeroRate - ratePagate;
    const interessiRateFuture = interessiPerRata * rateFuture;
    const risparmio = richiestoDecadenza - importoAccordato;

    return {
      baseLabel: 'Risparmio',
      baseValue: round2(risparmio),
      outputs: {
        rata: round2(rata),
        rate_pagate: round2(ratePagate),
        rate_scadute_non_pagate: round2(rateScaduteNonPagate),
        debito_residuo_piano: round2(debitoResiduoPiano),
        interessi_rate_future: round2(interessiRateFuture),
        risparmio: round2(risparmio)
      }
    };
  }

  return { baseLabel: '', baseValue: 0, outputs: {} };
};

const buildDefaultCalcoloInputs = (ipotesiKey, row, verifyDraft) => {
  const importoChiusura = verifyDraft?.importo_chiusura ?? formatNumber2(row?.importo_chiusura, '');
  const importoAccordato = verifyDraft?.importo_accordato ?? formatNumber2(row?.importo_accordato, '');
  const importoInteressi = verifyDraft?.importo_interessi ?? formatNumber2(row?.importo_interessi, '');
  const dataChiusura = verifyDraft?.data_chiusura ?? (row?.data_chiusura ? String(row.data_chiusura).slice(0, 10) : '');

  if (ipotesiKey === 'saldo_stralcio') {
    return {
      importo_dovuto: importoChiusura,
      fonte_importo: 'Lettera del creditore',
      importo_accordato: importoAccordato,
      modalita_pagamento: 'Rateale',
      numero_rate: '',
      prima_scadenza: ''
    };
  }

  if (ipotesiKey === 'carta_revolving') {
    return {
      tipo_carta: 'Revolving',
      saldo_debito: importoChiusura,
      interessi_estratto_conto: importoInteressi,
      periodo_da: '',
      periodo_a: '',
      taeg: ''
    };
  }

  if (ipotesiKey === 'finanziamento_decaduto') {
    return {
      importo_erogato: '',
      totale_da_restituire: '',
      numero_rate: '',
      importo_pagato: '',
      importo_scaduto: '',
      data_decadenza: dataChiusura,
      richiesto_a_decadenza: importoChiusura,
      importo_accordato: importoAccordato
    };
  }

  if (ipotesiKey === 'piano_rientro') {
    return {
      importo_erogato: '',
      totale_da_restituire: importoChiusura,
      numero_rate: '',
      rate_pagate: '',
      data_prima_rata: '',
      data_ultima_pagata: ''
    };
  }

  return {};
};

const escapeHtml = (value) => {
  const s = String(value ?? '');
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const buildComunicazioneTableHtml = (rows, visibility = {}) => {
  const cols = {
    issuer_name: true,
    category_name: true,
    calcolo_ipotesi: true,
    data_chiusura: true,
    importo_chiusura: true,
    importo_accordato: true,
    risparmio: true,
    base_compenso: true,
    perc_transazione: true,
    importo_transazione: true,
    ...(visibility || {})
  };

  const safeRows = Array.isArray(rows) ? rows : [];
  const parseCalcoloDettagli = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch (_e) {
        return null;
      }
    }
    return null;
  };

  const getRowCalcoloData = (r) => {
    const dettagli = parseCalcoloDettagli(r?.calcolo_dettagli_json);
    const ipotesiKey =
      normalizeCalcoloIpotesiKey(dettagli?.ipotesi) || normalizeCalcoloIpotesiKey(r?.calcolo_ipotesi) || '';
    const computed =
      ipotesiKey && dettagli?.inputs && typeof dettagli.inputs === 'object'
        ? computeCalcoloIpotesi(ipotesiKey, dettagli.inputs)
        : null;
    const baseLabel = dettagli?.base?.label || computed?.baseLabel || '';
    const baseValueRaw =
      dettagli?.base?.value != null
        ? Number(dettagli.base.value)
        : computed?.baseValue != null
          ? Number(computed.baseValue)
          : parseItNumber(r?.base_compenso);
    const baseValue = Number.isFinite(baseValueRaw) ? baseValueRaw : null;
    return { dettagli, ipotesiKey, computed, baseLabel, baseValue };
  };

  const buildDettaglioRowHtml = (r) => {
    const { dettagli, ipotesiKey, computed, baseLabel, baseValue } = getRowCalcoloData(r);
    const label = getCalcoloIpotesiLabel(ipotesiKey) || (ipotesiKey ? ipotesiKey : '');
    const money = (v, fallback = '') => escapeHtml(formatNumber2It(v, fallback));
    const perc = (v, fallback = '') => escapeHtml(formatNumber2It(v, fallback));
    const date = (v) => escapeHtml(String(v || ''));
    const inputs = dettagli?.inputs && typeof dettagli.inputs === 'object' ? dettagli.inputs : {};
    const outputs = (computed?.outputs && typeof computed.outputs === 'object' ? computed.outputs : dettagli?.outputs) || {};

    const lines = [];
    if (label) lines.push(`<strong>Calcolo:</strong> ${escapeHtml(label)}`);
    if (baseLabel || baseValue != null) {
      const baseLabelHtml = baseLabel ? ` (${escapeHtml(baseLabel)})` : '';
      const baseValHtml = baseValue != null ? `${money(baseValue)} €` : '';
      lines.push(`<strong>Base Compenso</strong>${baseLabelHtml}: ${baseValHtml}`.trim());
    }
    lines.push(`<strong>Formula:</strong> Importo Transazione = Base Compenso × (Compenso % / 100)`);

    if (ipotesiKey === 'saldo_stralcio') {
      lines.push(`<strong>Importo dovuto:</strong> ${money(parseFlexibleNumber(inputs.importo_dovuto), '')} €`);
      lines.push(`<strong>Importo accordato:</strong> ${money(parseFlexibleNumber(inputs.importo_accordato), '')} €`);
      if (outputs?.sconto_percent != null) lines.push(`<strong>Sconto:</strong> ${perc(outputs.sconto_percent)} %`);
      if (outputs?.rata != null) lines.push(`<strong>Rata:</strong> ${money(outputs.rata)} €`);
    } else if (ipotesiKey === 'carta_revolving') {
      lines.push(`<strong>Saldo debito:</strong> ${money(parseFlexibleNumber(inputs.saldo_debito), '')} €`);
      lines.push(`<strong>Interessi estratto conto:</strong> ${money(parseFlexibleNumber(inputs.interessi_estratto_conto), '')} €`);
      if (outputs?.capitale_netto_interessi != null) lines.push(`<strong>Capitale netto interessi:</strong> ${money(outputs.capitale_netto_interessi)} €`);
      if (outputs?.incidenza_interessi_percent != null) lines.push(`<strong>Incidenza interessi:</strong> ${perc(outputs.incidenza_interessi_percent)} %`);
    } else if (ipotesiKey === 'piano_rientro') {
      lines.push(`<strong>Importo erogato:</strong> ${money(parseFlexibleNumber(inputs.importo_erogato), '')} €`);
      lines.push(`<strong>Totale da restituire:</strong> ${money(parseFlexibleNumber(inputs.totale_da_restituire), '')} €`);
      if (inputs.numero_rate) lines.push(`<strong>N. rate:</strong> ${escapeHtml(String(inputs.numero_rate))}`);
      if (inputs.rate_pagate) lines.push(`<strong>Rate pagate:</strong> ${escapeHtml(String(inputs.rate_pagate))}`);
      if (inputs.data_prima_rata) lines.push(`<strong>Prima rata:</strong> ${date(inputs.data_prima_rata)}`);
      if (outputs?.rata != null) lines.push(`<strong>Rata:</strong> ${money(outputs.rata)} €`);
      if (outputs?.interessi_totali != null) lines.push(`<strong>Interessi totali:</strong> ${money(outputs.interessi_totali)} €`);
      if (outputs?.interessi_gia_pagati != null) lines.push(`<strong>Interessi già pagati:</strong> ${money(outputs.interessi_gia_pagati)} €`);
      if (outputs?.interessi_risparmiati != null) lines.push(`<strong>Interessi risparmiati:</strong> ${money(outputs.interessi_risparmiati)} €`);
    } else if (ipotesiKey === 'finanziamento_decaduto') {
      if (inputs.data_decadenza) lines.push(`<strong>Data decadenza:</strong> ${date(inputs.data_decadenza)}`);
      lines.push(`<strong>Richiesto a decadenza:</strong> ${money(parseFlexibleNumber(inputs.richiesto_a_decadenza), '')} €`);
      lines.push(`<strong>Importo accordato:</strong> ${money(parseFlexibleNumber(inputs.importo_accordato), '')} €`);
      if (inputs.totale_da_restituire) lines.push(`<strong>Totale da restituire:</strong> ${money(parseFlexibleNumber(inputs.totale_da_restituire), '')} €`);
      if (inputs.importo_pagato) lines.push(`<strong>Importo pagato:</strong> ${money(parseFlexibleNumber(inputs.importo_pagato), '')} €`);
      if (outputs?.rata != null) lines.push(`<strong>Rata:</strong> ${money(outputs.rata)} €`);
      if (outputs?.debito_residuo_piano != null) lines.push(`<strong>Debito residuo (piano):</strong> ${money(outputs.debito_residuo_piano)} €`);
      if (outputs?.interessi_rate_future != null) lines.push(`<strong>Interessi rate future:</strong> ${money(outputs.interessi_rate_future)} €`);
    }

    return `<div style="font-size:12px;line-height:1.35;">${lines.map((x) => `<div>${x}</div>`).join('')}</div>`;
  };

  const totals = safeRows.reduce(
    (acc, r) => ({
      importo_chiusura: acc.importo_chiusura + Number(parseItNumber(r.importo_chiusura) || 0),
      importo_accordato: acc.importo_accordato + Number(parseItNumber(r.importo_accordato) || 0),
      risparmio: acc.risparmio + Number(parseItNumber(r.risparmio) || 0),
      base_compenso:
        acc.base_compenso +
        (() => {
          const { baseValue } = getRowCalcoloData(r);
          return Number(baseValue || 0);
        })(),
      importo_transazione: acc.importo_transazione + Number(parseItNumber(r.importo_transazione) || 0)
    }),
    { importo_chiusura: 0, importo_accordato: 0, risparmio: 0, base_compenso: 0, importo_transazione: 0 }
  );

  const th = (label, align = 'left') =>
    `<th style="padding:6px 8px;border:1px solid #e5e7eb;text-align:${align};background:#f9fafb;">${escapeHtml(label)}</th>`;

  const td = (content, align = 'left') =>
    `<td style="padding:6px 8px;border:1px solid #e5e7eb;text-align:${align};">${content}</td>`;

  const headerHtml = [
    th('Pratica'),
    cols.issuer_name ? th('Creditore') : '',
    cols.category_name ? th('Tipologia') : '',
    cols.calcolo_ipotesi ? th('Calcolo') : '',
    cols.data_chiusura ? th('Data chiusura') : '',
    cols.importo_chiusura ? th('Importo chiusura', 'right') : '',
    cols.importo_accordato ? th('Importo accordato', 'right') : '',
    cols.risparmio ? th('Risparmio', 'right') : '',
    cols.base_compenso ? th('Base Compenso', 'right') : '',
    cols.perc_transazione ? th('% Trans.', 'right') : '',
    cols.importo_transazione ? th('Importo trans.', 'right') : ''
  ]
    .filter(Boolean)
    .join('');

  const colCount =
    1 +
    (cols.issuer_name ? 1 : 0) +
    (cols.category_name ? 1 : 0) +
    (cols.calcolo_ipotesi ? 1 : 0) +
    (cols.data_chiusura ? 1 : 0) +
    (cols.importo_chiusura ? 1 : 0) +
    (cols.importo_accordato ? 1 : 0) +
    (cols.risparmio ? 1 : 0) +
    (cols.base_compenso ? 1 : 0) +
    (cols.perc_transazione ? 1 : 0) +
    (cols.importo_transazione ? 1 : 0);

  const rowsHtml = safeRows
    .map((r) => {
      const { ipotesiKey, baseValue } = getRowCalcoloData(r);
      const praticaId = escapeHtml(r.id);
      const creditore = escapeHtml(r.issuer_name || '');
      const tipologia = escapeHtml(r.category_name || '');
      const calcolo = escapeHtml(getCalcoloIpotesiLabel(ipotesiKey) || '');
      const dataChiusura = escapeHtml(r.data_chiusura || '');
      const importoChiusura = escapeHtml(formatNumber2It(parseItNumber(r.importo_chiusura), r.importo_chiusura || ''));
      const importoAccordato = escapeHtml(formatNumber2It(parseItNumber(r.importo_accordato), r.importo_accordato || ''));
      const risparmio = escapeHtml(formatNumber2It(parseItNumber(r.risparmio), r.risparmio || ''));
      const baseCompenso = escapeHtml(formatNumber2It(baseValue, r.base_compenso || ''));
      const perc = escapeHtml(formatNumber2It(parseItNumber(r.perc_transazione), r.perc_transazione || ''));
      const importoTrans = escapeHtml(formatNumber2It(parseItNumber(r.importo_transazione), r.importo_transazione || ''));

      const cells = [
        td(praticaId),
        cols.issuer_name ? td(creditore) : '',
        cols.category_name ? td(tipologia) : '',
        cols.calcolo_ipotesi ? td(calcolo) : '',
        cols.data_chiusura ? td(dataChiusura) : '',
        cols.importo_chiusura ? td(importoChiusura, 'right') : '',
        cols.importo_accordato ? td(importoAccordato, 'right') : '',
        cols.risparmio ? td(risparmio, 'right') : '',
        cols.base_compenso ? td(baseCompenso, 'right') : '',
        cols.perc_transazione ? td(perc, 'right') : '',
        cols.importo_transazione ? td(importoTrans, 'right') : ''
      ]
        .filter(Boolean)
        .join('');

      const detailHtml = buildDettaglioRowHtml(r);
      return `<tr>${cells}</tr><tr><td colspan="${colCount}" style="padding:8px;border:1px solid #e5e7eb;background:#fcfcfd;">${detailHtml}</td></tr>`;
    })
    .join('');

  const nonNumericSpan =
    1 +
    (cols.issuer_name ? 1 : 0) +
    (cols.category_name ? 1 : 0) +
    (cols.calcolo_ipotesi ? 1 : 0) +
    (cols.data_chiusura ? 1 : 0);

  const totalsCells = [
    `<td colspan="${nonNumericSpan}" style="padding:8px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:700;">Totale</td>`,
    cols.importo_chiusura
      ? `<td style="padding:8px;border:1px solid #e5e7eb;background:#f9fafb;text-align:right;font-weight:700;">${escapeHtml(formatNumber2It(totals.importo_chiusura))}</td>`
      : '',
    cols.importo_accordato
      ? `<td style="padding:8px;border:1px solid #e5e7eb;background:#f9fafb;text-align:right;font-weight:700;">${escapeHtml(formatNumber2It(totals.importo_accordato))}</td>`
      : '',
    cols.risparmio
      ? `<td style="padding:8px;border:1px solid #e5e7eb;background:#f9fafb;text-align:right;font-weight:700;">${escapeHtml(formatNumber2It(totals.risparmio))}</td>`
      : '',
    cols.base_compenso
      ? `<td style="padding:8px;border:1px solid #e5e7eb;background:#f9fafb;text-align:right;font-weight:700;">${escapeHtml(formatNumber2It(totals.base_compenso))}</td>`
      : '',
    cols.perc_transazione ? `<td style="padding:8px;border:1px solid #e5e7eb;background:#f9fafb;"></td>` : '',
    cols.importo_transazione
      ? `<td style="padding:8px;border:1px solid #e5e7eb;background:#f9fafb;text-align:right;font-weight:700;">${escapeHtml(
          formatNumber2It(totals.importo_transazione)
        )}</td>`
      : ''
  ]
    .filter(Boolean)
    .join('');

  return `
    <table style="border-collapse:collapse;width:100%;margin:12px 0;font-family:Arial, sans-serif;font-size:13px;">
      <thead><tr>${headerHtml}</tr></thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot><tr>${totalsCells}</tr></tfoot>
    </table>
  `;
};

const buildPianoRatealeTableHtml = (rateRows) => {
  const safeRows = Array.isArray(rateRows) ? rateRows : [];
  const total = safeRows.reduce((sum, r) => sum + Number(parseItNumber(r?.amount) || 0), 0);
  const rowsHtml = safeRows
    .map((r) => {
      const idx = escapeHtml(r?.idx);
      const dueDate = escapeHtml(r?.due_date || '');
      const amount = escapeHtml(formatNumber2It(parseItNumber(r?.amount), r?.amount || ''));
      return `
        <tr>
          <td style="padding:6px 8px;border:1px solid #e5e7eb;text-align:right;">${idx}</td>
          <td style="padding:6px 8px;border:1px solid #e5e7eb;">${dueDate}</td>
          <td style="padding:6px 8px;border:1px solid #e5e7eb;text-align:right;">${amount}</td>
        </tr>
      `;
    })
    .join('');

  return `
    <table style="border-collapse:collapse;width:100%;margin:12px 0;font-family:Arial, sans-serif;font-size:13px;">
      <thead>
        <tr>
          <th style="padding:6px 8px;border:1px solid #e5e7eb;text-align:right;background:#f9fafb;">N.</th>
          <th style="padding:6px 8px;border:1px solid #e5e7eb;text-align:left;background:#f9fafb;">Scadenza</th>
          <th style="padding:6px 8px;border:1px solid #e5e7eb;text-align:right;background:#f9fafb;">Importo</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot>
        <tr>
          <td colspan="2" style="padding:8px;border:1px solid #e5e7eb;background:#f9fafb;font-weight:700;">Totale</td>
          <td style="padding:8px;border:1px solid #e5e7eb;background:#f9fafb;text-align:right;font-weight:700;">${escapeHtml(
            formatNumber2It(total)
          )}</td>
        </tr>
      </tfoot>
    </table>
  `;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const normalizeParams = (filters) => {
  const params = {};
  Object.entries(filters || {}).forEach(([key, value]) => {
    const arr = toArray(value);
    const targetKey = key === 'incassatoPercentRange' ? 'incassato_percent_range' : key;
    if (arr.length > 0) params[targetKey] = arr.join(',');
    else if (typeof value === 'string' && value.trim()) params[targetKey] = value.trim();
  });
  return params;
};

const quillModules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'font',
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'align',
  'list',
  'bullet',
  'indent',
  'blockquote',
  'code-block',
  'link'
];

const getCustomerDisplayName = (row) =>
  row.customer_ragione_sociale ||
  `${row.customer_name || ''} ${row.customer_surname || ''}`.trim() ||
  '-';

const hashString = (value) => {
  const s = String(value || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const getClusterTone = (theme, label) => {
  const tones = [
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.secondary.main,
    theme.palette.primary.main,
    theme.palette.error.main
  ];
  const idx = hashString(label) % tones.length;
  return tones[idx];
};

const twoLineHeader = (line1, line2) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.05 }}>
    <span>{line1}</span>
    <span style={{ fontSize: 11, opacity: 0.75 }}>{line2}</span>
  </Box>
);

export default function TransazioniCalcolo() {
  const navigate = useNavigate();
  const { addJob } = useJob() || {};
  const { user } = useAuth() || {};
  const isAdminUser = String(user?.role || '').toLowerCase() === 'admin';
  const contabileFullName = useMemo(() => [user?.name, user?.surname].filter(Boolean).join(' ').trim(), [user?.name, user?.surname]);
  const [workflowReportLoading, setWorkflowReportLoading] = useState(false);
  const [filters, setFilters] = useState(loadDefaultFilters());
  const [filterOptions, setFilterOptions] = useState({
    responsabiliLegali: [],
    responsabiliContabili: [],
    controparti: [],
    tipologie: [],
    clusters: [],
    modalitaChiusura: [],
    statuses: [],
    statoVerifica: []
  });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    totalePratiche: 0,
    totaleRisparmio: 0,
    totaleCompensoDovuto: 0,
    totaleCompensoAutorizzato: 0
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('data_chiusura');
  const [order, setOrder] = useState('desc');
  const [viewTab, setViewTab] = useState('list');

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);

  const getDefaultFilters = useCallback(() => {
    const base = loadDefaultFilters();
    if (!isAdminUser && contabileFullName) return { ...base, responsabileContabile: [contabileFullName] };
    return base;
  }, [contabileFullName, isAdminUser]);

  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyRow, setVerifyRow] = useState(null);
  const [verifyForm, setVerifyForm] = useState({
    data_chiusura: '',
    importo_chiusura: '',
    importo_accordato: '',
    importo_interessi: '',
    perc_transazione: '',
    importo_transazione: ''
  });
  const [calcoloIpotesi, setCalcoloIpotesi] = useState('saldo_stralcio');
  const [calcoloIpotesiSaved, setCalcoloIpotesiSaved] = useState(null);
  const [calcoloInputsByIpotesi, setCalcoloInputsByIpotesi] = useState({});
  const [importoTransazioneAuto, setImportoTransazioneAuto] = useState(true);
  const [calcoloDirty, setCalcoloDirty] = useState(false);
  const [calcoloIpotesiChangeDialogOpen, setCalcoloIpotesiChangeDialogOpen] = useState(false);
  const [pendingCalcoloIpotesi, setPendingCalcoloIpotesi] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [unverifyForceDialogOpen, setUnverifyForceDialogOpen] = useState(false);
  const [unverifyBlockInfo, setUnverifyBlockInfo] = useState(null);

  const [comunicazioneLoading, setComunicazioneLoading] = useState(false);
  const [comunicazioneRows, setComunicazioneRows] = useState([]);
  const [comunicazioneTotal, setComunicazioneTotal] = useState(0);
  const [comunicazionePage, setComunicazionePage] = useState(0);
  const [comunicazioneRowsPerPage, setComunicazioneRowsPerPage] = useState(10);
  const [comunicazioneExpanded, setComunicazioneExpanded] = useState({});
  const [comunicazioneSelected, setComunicazioneSelected] = useState({});
  const [comunicazioneSelectAllAnchorEl, setComunicazioneSelectAllAnchorEl] = useState(null);
  const [comunicazioneBulkDialogOpen, setComunicazioneBulkDialogOpen] = useState(false);
  const [comunicazioneBulkAccountId, setComunicazioneBulkAccountId] = useState(null);
  const [comunicazioneBulkSending, setComunicazioneBulkSending] = useState(false);
  const [comunicazioneBulkProgress, setComunicazioneBulkProgress] = useState({ done: 0, total: 0 });
  const [comunicazioneShowSent, setComunicazioneShowSent] = useState(false);

  const [sollecitoLoading, setSollecitoLoading] = useState(false);
  const [sollecitoRows, setSollecitoRows] = useState([]);
  const [sollecitoTotal, setSollecitoTotal] = useState(0);
  const [sollecitoPage, setSollecitoPage] = useState(0);
  const [sollecitoRowsPerPage, setSollecitoRowsPerPage] = useState(10);
  const [sollecitoExpanded, setSollecitoExpanded] = useState({});
  const [sollecitoSelected, setSollecitoSelected] = useState({});
  const [sollecitoSelectAllAnchorEl, setSollecitoSelectAllAnchorEl] = useState(null);
  const [sollecitoBulkDialogOpen, setSollecitoBulkDialogOpen] = useState(false);
  const [sollecitoBulkAccountId, setSollecitoBulkAccountId] = useState(null);
  const [sollecitoBulkSending, setSollecitoBulkSending] = useState(false);
  const [sollecitoBulkProgress, setSollecitoBulkProgress] = useState({ done: 0, total: 0 });
  const [sollecitoShowExcluded, setSollecitoShowExcluded] = useState(false);
  const [sollecitoShowSollecitati, setSollecitoShowSollecitati] = useState(false);
  const [sollecitoMinDaysBetween, setSollecitoMinDaysBetween] = useState(7);

  const [pianoRatealeLoading, setPianoRatealeLoading] = useState(false);
  const [pianoRatealeRows, setPianoRatealeRows] = useState([]);
  const [pianoRatealeTotal, setPianoRatealeTotal] = useState(0);
  const [pianoRatealePage, setPianoRatealePage] = useState(0);
  const [pianoRatealeRowsPerPage, setPianoRatealeRowsPerPage] = useState(10);
  const [pianoRatealeExpanded, setPianoRatealeExpanded] = useState({});
  const [pianoRatealeSelected, setPianoRatealeSelected] = useState({});
  const [pianoRatealeSelectAllAnchorEl, setPianoRatealeSelectAllAnchorEl] = useState(null);
  const [pianoRatealeBulkDialogOpen, setPianoRatealeBulkDialogOpen] = useState(false);
  const [pianoRatealeBulkAccountId, setPianoRatealeBulkAccountId] = useState(null);
  const [pianoRatealeBulkSending, setPianoRatealeBulkSending] = useState(false);
  const [pianoRatealeBulkProgress, setPianoRatealeBulkProgress] = useState({ done: 0, total: 0 });
  const [pianoRatealeShowSent, setPianoRatealeShowSent] = useState(false);

  const [pianoRatealeEmailDialogOpen, setPianoRatealeEmailDialogOpen] = useState(false);
  const [pianoRatealeEmailDialogLoading, setPianoRatealeEmailDialogLoading] = useState(false);
  const [pianoRatealeEmailTarget, setPianoRatealeEmailTarget] = useState(null);
  const [pianoRatealeEmailPraticaIds, setPianoRatealeEmailPraticaIds] = useState([]);
  const [pianoRatealeViewDialogOpen, setPianoRatealeViewDialogOpen] = useState(false);
  const [pianoRatealeViewDialogLoading, setPianoRatealeViewDialogLoading] = useState(false);
  const [pianoRatealeViewData, setPianoRatealeViewData] = useState(null);
  const [pianoRatealeEmailForm, setPianoRatealeEmailForm] = useState({
    email_account_id: null,
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body_intro_html: '',
    body_footer_html: '',
    rate_plan: {
      total_amount: '',
      rate_count: 10,
      first_due_date: '',
      frequency: 'mensile'
    },
    rate_rows: []
  });

  const [sollecitoEmailDialogOpen, setSollecitoEmailDialogOpen] = useState(false);
  const [sollecitoEmailDialogLoading, setSollecitoEmailDialogLoading] = useState(false);
  const [sollecitoEmailTarget, setSollecitoEmailTarget] = useState(null);
  const [sollecitoEmailForm, setSollecitoEmailForm] = useState({
    email_account_id: null,
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body_html: '',
    pratica_ids: []
  });

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogLoading, setEmailDialogLoading] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailAccountsLoading, setEmailAccountsLoading] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailColumnsAnchorEl, setEmailColumnsAnchorEl] = useState(null);
  const [emailColumnVisibility, setEmailColumnVisibility] = useState({
    issuer_name: true,
    category_name: true,
    calcolo_ipotesi: true,
    data_chiusura: true,
    importo_chiusura: true,
    importo_accordato: true,
    risparmio: true,
    base_compenso: true,
    perc_transazione: true,
    importo_transazione: true
  });
  const [emailForm, setEmailForm] = useState({
    email_account_id: null,
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body_intro_html: '',
    body_footer_html: '',
    table_rows: []
  });
  const [emailDettagliExpanded, setEmailDettagliExpanded] = useState({});

  const [columnVisibility, setColumnVisibility] = useState({
    responsabileLegale: true,
    responsabileContabile: true,
    dataAssegnazione: true,
    cliente: true,
    cluster: true,
    praticheDaChiudere: true,
    ultimaFattura: true,
    contrattiImporto: true,
    upsellImporto: true,
    incassatoImporto: true,
    incassatoPercent: true,
    controparte: true,
    tipologia: true,
    statoAttuale: true,
    dataChiusura: true,
    giorniChiusura: true,
    importoChiusura: true,
    importoAccordato: true,
    percentualeRisparmio: true,
    modalitaChiusura: true,
    baseCalcoloTransazione: true,
    percTransazione: true,
    importoTransazioneCalcolata: true,
    percTransazioneApplicata: true,
    importoTransazioneAutorizzata: true
  });

  const totalColumns = useMemo(() => Object.values(columnVisibility).filter(Boolean).length + 1, [columnVisibility]);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await api.get('/negotiation/transazioni-calcolo/filters');
      setFilterOptions({
        responsabiliLegali: res.data?.responsabiliLegali || [],
        responsabiliContabili: res.data?.responsabiliContabili || [],
        controparti: res.data?.controparti || [],
        tipologie: res.data?.tipologie || [],
        clusters: res.data?.clusters || [],
        modalitaChiusura: res.data?.modalitaChiusura || [],
        statuses: res.data?.statuses || [],
        statoVerifica: res.data?.statoVerifica || []
      });
    } catch (err) {
      toast.error('Errore caricamento filtri');
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        order,
        ...normalizeParams(filters)
      };
      const res = await api.get('/negotiation/transazioni-calcolo', { params });
      setRows(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setStats(res.data?.stats || {});
    } catch (err) {
      toast.error('Errore caricamento transazioni');
    } finally {
      setLoading(false);
    }
  }, [filters, order, orderBy, page, rowsPerPage]);

  const startWorkflowReportJob = async () => {
    if (workflowReportLoading) return;
    setWorkflowReportLoading(true);
    try {
      const payload = { ...normalizeParams(filters) };
      const res = await api.post('/negotiation/transazioni-calcolo/report-job', payload);
      const jobId = res.data?.jobId;
      if (!jobId) {
        toast.error('Impossibile avviare il report');
        return;
      }
      if (typeof addJob === 'function') {
        addJob(jobId, 'transazioni_calcolo_workflow_pdf');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore avvio report');
    } finally {
      setWorkflowReportLoading(false);
    }
  };

  const fetchEmailAccounts = useCallback(async () => {
    setEmailAccountsLoading(true);
    try {
      const res = await api.get('/email-accounts/senders');
      const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      setEmailAccounts(list);
      return list;
    } catch (_) {
      setEmailAccounts([]);
      return [];
    } finally {
      setEmailAccountsLoading(false);
    }
  }, []);

  const getCalcoloStatoAttuale = (row) => {
    if (String(row?.stato_movimento || '') !== 'verificato') {
      return { key: 'da_verificare', label: 'Da verificare' };
    }
    if (row?.last_piano_rateale_il) {
      return { key: 'piano_rateale_inviato', label: 'Piano rateale inviato' };
    }
    if (row?.last_sollecito_il) {
      return { key: 'sollecito_inviato', label: 'Sollecito inviato' };
    }
    if (row?.last_comunicazione_il) {
      return { key: 'comunicazione_inviata', label: 'Comunicazione inviata' };
    }
    return { key: 'da_inviare_comunicazione', label: 'Da inviare comunicazione' };
  };

  const fetchComunicazione = useCallback(async () => {
    if (viewTab !== 'comunicazione') return;
    setComunicazioneLoading(true);
    try {
      const params = {
        page: comunicazionePage + 1,
        limit: comunicazioneRowsPerPage,
        include_sent: comunicazioneShowSent ? 1 : 0,
        ...normalizeParams(filters)
      };
      const res = await api.get('/negotiation/transazioni-calcolo/comunicazione', { params });
      setComunicazioneRows(res.data?.data || []);
      setComunicazioneTotal(res.data?.total || 0);
    } catch (err) {
      toast.error('Errore caricamento comunicazioni');
    } finally {
      setComunicazioneLoading(false);
    }
  }, [comunicazionePage, comunicazioneRowsPerPage, comunicazioneShowSent, filters, viewTab]);

  const fetchSollecito = useCallback(async () => {
    if (viewTab !== 'sollecito') return;
    setSollecitoLoading(true);
    try {
      const params = {
        page: sollecitoPage + 1,
        limit: sollecitoRowsPerPage,
        include_excluded: sollecitoShowExcluded || sollecitoShowSollecitati ? 1 : 0,
        show_sollecitati: sollecitoShowSollecitati ? 1 : 0,
        ...normalizeParams(filters)
      };
      const res = await api.get('/negotiation/transazioni-calcolo/sollecito', { params });
      setSollecitoRows(res.data?.data || []);
      setSollecitoTotal(res.data?.total || 0);
      setSollecitoMinDaysBetween(Number(res.data?.minDaysBetween || 7) || 7);
    } catch (err) {
      toast.error('Errore caricamento solleciti');
    } finally {
      setSollecitoLoading(false);
    }
  }, [filters, sollecitoPage, sollecitoRowsPerPage, sollecitoShowExcluded, sollecitoShowSollecitati, viewTab]);

  const fetchPianoRateale = useCallback(async () => {
    if (viewTab !== 'piano_rateale') return;
    setPianoRatealeLoading(true);
    try {
      const params = {
        page: pianoRatealePage + 1,
        limit: pianoRatealeRowsPerPage,
        include_sent: pianoRatealeShowSent ? 1 : 0,
        ...normalizeParams(filters)
      };
      const res = await api.get('/negotiation/transazioni-calcolo/piano-rateale', { params });
      setPianoRatealeRows(res.data?.data || []);
      setPianoRatealeTotal(res.data?.total || 0);
    } catch (err) {
      toast.error('Errore caricamento piano rateale');
    } finally {
      setPianoRatealeLoading(false);
    }
  }, [filters, pianoRatealePage, pianoRatealeRowsPerPage, pianoRatealeShowSent, viewTab]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    if (isAdminUser) return;
    if (!contabileFullName) return;
    setFilters((prev) => {
      const prevValue = Array.isArray(prev?.responsabileContabile) ? prev.responsabileContabile : [];
      if (prevValue.length === 1 && prevValue[0] === contabileFullName) return prev;
      return { ...prev, responsabileContabile: [contabileFullName] };
    });
  }, [contabileFullName, isAdminUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchComunicazione();
  }, [fetchComunicazione]);

  useEffect(() => {
    fetchSollecito();
  }, [fetchSollecito]);

  useEffect(() => {
    fetchPianoRateale();
  }, [fetchPianoRateale]);

  useEffect(() => {
    if (viewTab !== 'comunicazione') return;
    setComunicazionePage(0);
  }, [filters, viewTab]);

  useEffect(() => {
    if (viewTab !== 'sollecito') return;
    setSollecitoPage(0);
  }, [filters, viewTab]);

  useEffect(() => {
    if (viewTab !== 'piano_rateale') return;
    setPianoRatealePage(0);
  }, [filters, viewTab]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.statoVerifica?.length) chips.push({ label: `Stato Verifica: ${filters.statoVerifica.join(', ')}`, key: 'statoVerifica' });
    if (filters.status?.length) chips.push({ label: `Stato: ${filters.status.join(', ')}`, key: 'status' });
    if (filters.responsabileLegale?.length) chips.push({ label: `Resp. legale: ${filters.responsabileLegale.join(', ')}`, key: 'responsabileLegale' });
    if (filters.responsabileContabile?.length)
      chips.push({ label: `Resp. contabile: ${filters.responsabileContabile.join(', ')}`, key: 'responsabileContabile', locked: !isAdminUser });
    if (filters.cluster?.length) chips.push({ label: `Cluster: ${filters.cluster.join(', ')}`, key: 'cluster' });
    if (filters.ultimaFatturaAge) {
      const label = ULTIMA_FATTURA_AGE_OPTIONS.find((o) => o.value === filters.ultimaFatturaAge)?.label || filters.ultimaFatturaAge;
      chips.push({ label: `Ultima fattura: ${label}`, key: 'ultimaFatturaAge' });
    }
    if (filters.incassatoPercentRange) {
      const label =
        INCASSATO_PERCENT_RANGE_OPTIONS.find((o) => o.value === filters.incassatoPercentRange)?.label || filters.incassatoPercentRange;
      chips.push({ label: `% sul totale: ${label}`, key: 'incassatoPercentRange' });
    }
    if (filters.dataChiusuraFrom) chips.push({ label: `chiuse dal: ${formatDate(filters.dataChiusuraFrom)}`, key: 'dataChiusuraFrom' });
    if (filters.dataChiusuraTo) chips.push({ label: `al: ${formatDate(filters.dataChiusuraTo)}`, key: 'dataChiusuraTo' });
    if (filters.importoAccordatoFilter) {
      const label = IMPORTO_ACCORDATO_FILTER_OPTIONS.find((o) => o.value === filters.importoAccordatoFilter)?.label || filters.importoAccordatoFilter;
      chips.push({ label: `Importo Accordato: ${label}`, key: 'importoAccordatoFilter' });
    }
    return chips;
  }, [filters, isAdminUser]);

  const handleDeleteActiveFilter = (key) => {
    setPage(0);
    if (key === 'statoVerifica') {
      setFilters((prev) => ({ ...prev, statoVerifica: [] }));
      return;
    }
    if (key === 'status') {
      setFilters((prev) => ({ ...prev, status: [] }));
      return;
    }
    if (key === 'responsabileLegale') {
      setFilters((prev) => ({ ...prev, responsabileLegale: [] }));
      return;
    }
    if (key === 'responsabileContabile') {
      if (!isAdminUser) return;
      setFilters((prev) => ({ ...prev, responsabileContabile: [] }));
      return;
    }
    if (key === 'cluster') {
      setFilters((prev) => ({ ...prev, cluster: [] }));
      return;
    }
    if (key === 'ultimaFatturaAge') {
      setFilters((prev) => ({ ...prev, ultimaFatturaAge: '' }));
      return;
    }
    if (key === 'incassatoPercentRange') {
      setFilters((prev) => ({ ...prev, incassatoPercentRange: '' }));
      return;
    }
    if (key === 'dataChiusuraFrom') {
      setFilters((prev) => ({ ...prev, dataChiusuraFrom: '' }));
      return;
    }
    if (key === 'dataChiusuraTo') {
      setFilters((prev) => ({ ...prev, dataChiusuraTo: '' }));
      return;
    }
    if (key === 'importoAccordatoFilter') {
      setFilters((prev) => ({ ...prev, importoAccordatoFilter: '' }));
    }
  };

  const handleComunicazioneChangePage = (_event, newPage) => {
    setComunicazionePage(newPage);
  };

  const handleComunicazioneChangeRowsPerPage = (event) => {
    setComunicazioneRowsPerPage(parseInt(event.target.value, 10));
    setComunicazionePage(0);
  };

  const handleSollecitoChangePage = (_event, newPage) => {
    setSollecitoPage(newPage);
  };

  const handleSollecitoChangeRowsPerPage = (event) => {
    setSollecitoRowsPerPage(parseInt(event.target.value, 10));
    setSollecitoPage(0);
  };

  const handlePianoRatealeChangePage = (_event, newPage) => {
    setPianoRatealePage(newPage);
  };

  const handlePianoRatealeChangeRowsPerPage = (event) => {
    setPianoRatealeRowsPerPage(parseInt(event.target.value, 10));
    setPianoRatealePage(0);
  };

  const toggleComunicazioneExpanded = (customerId) => {
    const key = String(customerId || '');
    if (!key) return;
    setComunicazioneExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSollecitoExpanded = (customerId) => {
    const key = String(customerId || '');
    if (!key) return;
    setSollecitoExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePianoRatealeExpanded = (customerId) => {
    const key = String(customerId || '');
    if (!key) return;
    setPianoRatealeExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedComunicazioneIds = useMemo(
    () =>
      Object.entries(comunicazioneSelected || {})
        .filter(([, v]) => Boolean(v))
        .map(([k]) => Number(k))
        .filter((v) => Number.isFinite(v) && v > 0),
    [comunicazioneSelected]
  );

  const selectedSollecitoIds = useMemo(
    () =>
      Object.entries(sollecitoSelected || {})
        .filter(([, v]) => Boolean(v))
        .map(([k]) => Number(k))
        .filter((v) => Number.isFinite(v) && v > 0),
    [sollecitoSelected]
  );

  const selectedPianoRatealeIds = useMemo(
    () =>
      Object.entries(pianoRatealeSelected || {})
        .filter(([, v]) => Boolean(v))
        .map(([k]) => Number(k))
        .filter((v) => Number.isFinite(v) && v > 0),
    [pianoRatealeSelected]
  );

  const visibleSollecitoRows = useMemo(() => {
    if (!sollecitoShowSollecitati) return sollecitoRows || [];
    if (Array.isArray(sollecitoRows)) return sollecitoRows;
    return (sollecitoRows || []).filter((c) => {
      if (c?.last_inviato_il) return true;
      return Array.isArray(c?.pratiche) && c.pratiche.some((p) => Boolean(p?.last_sollecito_il));
    });
  }, [sollecitoRows, sollecitoShowSollecitati]);

  const visiblePianoRatealeRows = useMemo(() => {
    if (!pianoRatealeShowSent) return pianoRatealeRows || [];
    return (pianoRatealeRows || []).filter((c) => Number(c?.pending_pratiche_count || 0) > 0 || Boolean(c?.last_inviato_il));
  }, [pianoRatealeRows, pianoRatealeShowSent]);

  const comunicazioneSelectionState = useMemo(() => {
    const pageIds = (comunicazioneRows || [])
      .filter((c) => Number(c?.pending_pratiche_count || 0) > 0)
      .map((c) => Number(c?.customer_id))
      .filter((v) => Number.isFinite(v) && v > 0);
    if (!pageIds.length) return { all: false, some: false };
    let selectedOnPage = 0;
    pageIds.forEach((id) => {
      if (comunicazioneSelected?.[String(id)]) selectedOnPage += 1;
    });
    return {
      all: selectedOnPage === pageIds.length,
      some: selectedOnPage > 0 && selectedOnPage < pageIds.length
    };
  }, [comunicazioneRows, comunicazioneSelected]);

  const sollecitoSelectionState = useMemo(() => {
    const pageIds = (visibleSollecitoRows || [])
      .filter((c) => Number(c?.pending_pratiche_count || 0) > 0)
      .map((c) => Number(c?.customer_id))
      .filter((v) => Number.isFinite(v) && v > 0);
    if (!pageIds.length) return { all: false, some: false };
    let selectedOnPage = 0;
    pageIds.forEach((id) => {
      if (sollecitoSelected?.[String(id)]) selectedOnPage += 1;
    });
    return {
      all: selectedOnPage === pageIds.length,
      some: selectedOnPage > 0 && selectedOnPage < pageIds.length
    };
  }, [visibleSollecitoRows, sollecitoSelected]);

  const pianoRatealeSelectionState = useMemo(() => {
    const pageIds = (visiblePianoRatealeRows || [])
      .filter((c) => Number(c?.pending_pratiche_count || 0) > 0)
      .map((c) => Number(c?.customer_id))
      .filter((v) => Number.isFinite(v) && v > 0);
    if (!pageIds.length) return { all: false, some: false };
    let selectedOnPage = 0;
    pageIds.forEach((id) => {
      if (pianoRatealeSelected?.[String(id)]) selectedOnPage += 1;
    });
    return {
      all: selectedOnPage === pageIds.length,
      some: selectedOnPage > 0 && selectedOnPage < pageIds.length
    };
  }, [visiblePianoRatealeRows, pianoRatealeSelected]);

  const getSollecitoExcludedLabel = (reason) => {
    const key = String(reason || '').trim();
    if (key === 'piano_rateale_inviato') return 'Piano rateale già inviato';
    if (key === 'sollecito_recent') return `Sollecito < ${Number(sollecitoMinDaysBetween || 7)}gg`;
    if (key === 'comunicazione_mancante') return 'Comunicazione non inviata';
    if (key === 'non_verificato') return 'Transazione non verificata';
    return 'Non inviabile';
  };

  const getSollecitoCustomerDisabledTooltip = (customer) => {
    const pratiche = Array.isArray(customer?.pratiche) ? customer.pratiche : [];
    const reasons = new Set(
      pratiche
        .filter((p) => p && p.sendable === false && p.excluded_reason)
        .map((p) => String(p.excluded_reason || '').trim())
        .filter(Boolean)
    );

    if (reasons.has('sollecito_recent')) {
      return `Non sollecitabile: sollecito recente (min ${Number(sollecitoMinDaysBetween || 7)} giorni)`;
    }
    if (reasons.has('piano_rateale_inviato')) return 'Non sollecitabile: piano rateale già inviato';
    if (reasons.has('comunicazione_mancante')) return 'Non sollecitabile: comunicazione non inviata';
    if (reasons.has('non_verificato')) return 'Non sollecitabile: transazione non verificata';
    return 'Nessuna pratica sollecitabile';
  };

  const getComunicazioneExcludedLabel = (reason) => {
    const key = String(reason || '').trim();
    if (key === 'comunicazione_gia_inviata') return 'Già inviata';
    return 'Non inviabile';
  };

  const getPianoRatealeExcludedLabel = (reason) => {
    const key = String(reason || '').trim();
    if (key === 'piano_rateale_inviato') return 'Piano rateale già inviato';
    if (key === 'comunicazione_mancante') return 'Comunicazione non inviata';
    if (key === 'non_verificato') return 'Transazione non verificata';
    return 'Non inviabile';
  };

  const toggleComunicazioneSelected = (customerId) => {
    const id = Number(customerId);
    if (!Number.isFinite(id) || id <= 0) return;
    setComunicazioneSelected((prev) => ({ ...prev, [String(id)]: !prev?.[String(id)] }));
  };

  const toggleSollecitoSelected = (customerId) => {
    const id = Number(customerId);
    if (!Number.isFinite(id) || id <= 0) return;
    setSollecitoSelected((prev) => ({ ...prev, [String(id)]: !prev?.[String(id)] }));
  };

  const togglePianoRatealeSelected = (customerId) => {
    const id = Number(customerId);
    if (!Number.isFinite(id) || id <= 0) return;
    setPianoRatealeSelected((prev) => ({ ...prev, [String(id)]: !prev?.[String(id)] }));
  };

  const setComunicazioneSelectedForPage = (checked) => {
    setComunicazioneSelected((prev) => {
      const next = { ...(prev || {}) };
      (comunicazioneRows || []).forEach((c) => {
        const id = Number(c?.customer_id);
        if (!Number.isFinite(id) || id <= 0) return;
        if (Number(c?.pending_pratiche_count || 0) <= 0) return;
        next[String(id)] = Boolean(checked);
      });
      return next;
    });
  };

  const setSollecitoSelectedForPage = (checked) => {
    setSollecitoSelected((prev) => {
      const next = { ...(prev || {}) };
      (visibleSollecitoRows || []).forEach((c) => {
        const id = Number(c?.customer_id);
        if (!Number.isFinite(id) || id <= 0) return;
        if (Number(c?.pending_pratiche_count || 0) <= 0) return;
        next[String(id)] = Boolean(checked);
      });
      return next;
    });
  };

  const setPianoRatealeSelectedForPage = (checked) => {
    setPianoRatealeSelected((prev) => {
      const next = { ...(prev || {}) };
      (visiblePianoRatealeRows || []).forEach((c) => {
        const id = Number(c?.customer_id);
        if (!Number.isFinite(id) || id <= 0) return;
        if (Number(c?.pending_pratiche_count || 0) <= 0) return;
        next[String(id)] = Boolean(checked);
      });
      return next;
    });
  };

  const clearComunicazioneSelection = () => setComunicazioneSelected({});
  const clearSollecitoSelection = () => setSollecitoSelected({});
  const clearPianoRatealeSelection = () => setPianoRatealeSelected({});

  const fetchComunicazioneAllCustomerIds = useCallback(async () => {
    const params = normalizeParams(filters);
    const res = await api.get('/negotiation/transazioni-calcolo/comunicazione/ids', { params });
    const ids = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
    return ids.map((x) => Number(x)).filter((v) => Number.isFinite(v) && v > 0);
  }, [filters]);

  const fetchSollecitoAllCustomerIds = useCallback(async () => {
    const params = normalizeParams(filters);
    const res = await api.get('/negotiation/transazioni-calcolo/sollecito/ids', { params });
    const ids = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
    return ids.map((x) => Number(x)).filter((v) => Number.isFinite(v) && v > 0);
  }, [filters]);

  const fetchPianoRatealeAllCustomerIds = useCallback(async () => {
    const params = normalizeParams(filters);
    const res = await api.get('/negotiation/transazioni-calcolo/piano-rateale/ids', { params });
    const ids = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
    return ids.map((x) => Number(x)).filter((v) => Number.isFinite(v) && v > 0);
  }, [filters]);

  const selectAllComunicazioneFiltered = async () => {
    try {
      const ids = await fetchComunicazioneAllCustomerIds();
      const next = {};
      ids.forEach((id) => {
        next[String(id)] = true;
      });
      setComunicazioneSelected(next);
      toast.success(`Selezionati ${ids.length} clienti`);
    } catch (err) {
      toast.error('Errore selezione clienti');
    } finally {
      setComunicazioneSelectAllAnchorEl(null);
    }
  };

  const selectAllSollecitoFiltered = async () => {
    try {
      const ids = await fetchSollecitoAllCustomerIds();
      const next = {};
      ids.forEach((id) => {
        next[String(id)] = true;
      });
      setSollecitoSelected(next);
      toast.success(`Selezionati ${ids.length} clienti`);
    } catch (err) {
      toast.error('Errore selezione clienti');
    } finally {
      setSollecitoSelectAllAnchorEl(null);
    }
  };

  const selectAllPianoRatealeFiltered = async () => {
    try {
      const ids = await fetchPianoRatealeAllCustomerIds();
      const next = {};
      ids.forEach((id) => {
        next[String(id)] = true;
      });
      setPianoRatealeSelected(next);
      toast.success(`Selezionati ${ids.length} clienti`);
    } catch (err) {
      toast.error('Errore selezione clienti');
    } finally {
      setPianoRatealeSelectAllAnchorEl(null);
    }
  };

  const openComunicazioneBulkDialogForAllFiltered = async () => {
    try {
      const ids = await fetchComunicazioneAllCustomerIds();
      if (!ids.length) {
        toast.error('Nessun cliente da inviare con i filtri selezionati');
        return;
      }
      const next = {};
      ids.forEach((id) => {
        next[String(id)] = true;
      });
      setComunicazioneSelected(next);
      const accounts = emailAccounts.length ? emailAccounts : await fetchEmailAccounts();
      setComunicazioneBulkAccountId((prev) => prev ?? accounts?.[0]?.id ?? null);
      setComunicazioneBulkProgress({ done: 0, total: ids.length });
      setComunicazioneBulkDialogOpen(true);
    } catch (err) {
      toast.error('Errore caricamento clienti');
    }
  };

  const openSollecitoBulkDialogForAllFiltered = async () => {
    try {
      const ids = await fetchSollecitoAllCustomerIds();
      if (!ids.length) {
        toast.error('Nessun cliente da inviare con i filtri selezionati');
        return;
      }
      const next = {};
      ids.forEach((id) => {
        next[String(id)] = true;
      });
      setSollecitoSelected(next);
      const accounts = emailAccounts.length ? emailAccounts : await fetchEmailAccounts();
      setSollecitoBulkAccountId((prev) => prev ?? accounts?.[0]?.id ?? null);
      setSollecitoBulkProgress({ done: 0, total: ids.length });
      setSollecitoBulkDialogOpen(true);
    } catch (err) {
      toast.error('Errore caricamento clienti');
    }
  };

  const openPianoRatealeBulkDialogForAllFiltered = async () => {
    try {
      const ids = await fetchPianoRatealeAllCustomerIds();
      if (!ids.length) {
        toast.error('Nessun cliente da inviare con i filtri selezionati');
        return;
      }
      const next = {};
      ids.forEach((id) => {
        next[String(id)] = true;
      });
      setPianoRatealeSelected(next);
      const accounts = emailAccounts.length ? emailAccounts : await fetchEmailAccounts();
      setPianoRatealeBulkAccountId((prev) => prev ?? accounts?.[0]?.id ?? null);
      setPianoRatealeBulkProgress({ done: 0, total: ids.length });
      setPianoRatealeBulkDialogOpen(true);
    } catch (err) {
      toast.error('Errore caricamento clienti');
    }
  };

  const openComunicazioneBulkDialog = async () => {
    if (!selectedComunicazioneIds.length) {
      toast.error('Seleziona almeno un cliente');
      return;
    }
    const accounts = emailAccounts.length ? emailAccounts : await fetchEmailAccounts();
    setComunicazioneBulkAccountId((prev) => prev ?? accounts?.[0]?.id ?? null);
    setComunicazioneBulkProgress({ done: 0, total: selectedComunicazioneIds.length });
    setComunicazioneBulkDialogOpen(true);
  };

  const openPianoRatealeBulkDialog = async () => {
    if (!selectedPianoRatealeIds.length) {
      toast.error('Seleziona almeno un cliente');
      return;
    }
    const accounts = emailAccounts.length ? emailAccounts : await fetchEmailAccounts();
    setPianoRatealeBulkAccountId((prev) => prev ?? accounts?.[0]?.id ?? null);
    setPianoRatealeBulkProgress({ done: 0, total: selectedPianoRatealeIds.length });
    setPianoRatealeBulkDialogOpen(true);
  };

  const handleComunicazioneBulkSend = async () => {
    if (comunicazioneBulkSending) return;
    if (!selectedComunicazioneIds.length) return;
    if (!comunicazioneBulkAccountId) {
      toast.error('Seleziona un account mittente');
      return;
    }
    setComunicazioneBulkSending(true);
    setComunicazioneBulkProgress({ done: 0, total: selectedComunicazioneIds.length });
    try {
      const params = normalizeParams(filters);
      for (let i = 0; i < selectedComunicazioneIds.length; i += 1) {
        const customerId = selectedComunicazioneIds[i];
        const preview = await api.post(`/negotiation/transazioni-calcolo/comunicazione/${customerId}/preview`, {}, { params });
        const rows = Array.isArray(preview.data?.pratiche)
          ? preview.data.pratiche.map((p) => ({
              id: p.id,
              include: true,
              issuer_name: p.issuer_name || '',
              category_name: p.category_name || '',
              calcolo_ipotesi:
                normalizeCalcoloIpotesiKey(p?.calcolo_dettagli_json?.ipotesi) || normalizeCalcoloIpotesiKey(p?.calcolo_ipotesi) || '',
              data_chiusura: formatDate(p.data_chiusura),
              importo_chiusura: formatNumber2It(p.importo_chiusura, ''),
              importo_accordato: formatNumber2It(p.importo_accordato, ''),
              risparmio: formatNumber2It(p.base_calcolo_transazione, ''),
              base_compenso: (() => {
                const ipotesiKey =
                  normalizeCalcoloIpotesiKey(p?.calcolo_dettagli_json?.ipotesi) || normalizeCalcoloIpotesiKey(p?.calcolo_ipotesi) || '';
                const computed =
                  ipotesiKey && p?.calcolo_dettagli_json?.inputs
                    ? computeCalcoloIpotesi(ipotesiKey, p.calcolo_dettagli_json.inputs)
                    : null;
                const baseValueRaw =
                  p?.calcolo_dettagli_json?.base?.value != null
                    ? Number(p.calcolo_dettagli_json.base.value)
                    : computed?.baseValue != null
                      ? Number(computed.baseValue)
                      : null;
                return formatNumber2It(baseValueRaw, '');
              })(),
              perc_transazione: formatNumber2It(p.perc_transazione, ''),
              importo_transazione: formatNumber2It(p.importo_transazione_autorizzata, ''),
              calcolo_dettagli_json: p?.calcolo_dettagli_json ?? null
            }))
          : [];
        const selectedRows = rows.filter((r) => r?.include);
        if (!selectedRows.length) {
          setComunicazioneBulkProgress({ done: i + 1, total: selectedComunicazioneIds.length });
          continue;
        }
        const tableHtml = buildComunicazioneTableHtml(selectedRows, emailColumnVisibility);
        const bodyHtml = `${preview.data?.body_intro_html || ''}${tableHtml}${preview.data?.body_footer_html || ''}`;
        await api.post(
          `/negotiation/transazioni-calcolo/comunicazione/${customerId}/send`,
          {
            email_account_id: comunicazioneBulkAccountId,
            to: preview.data?.to_default || '',
            cc: '',
            bcc: '',
            subject: preview.data?.subject || 'Comunicazione transazione',
            body_html: bodyHtml,
            pratica_ids: selectedRows.map((r) => r.id),
            table_rows: selectedRows,
            table_columns: emailColumnVisibility
          },
          { params }
        );
        setComunicazioneBulkProgress({ done: i + 1, total: selectedComunicazioneIds.length });
      }
      toast.success('Invio completato');
      setComunicazioneBulkDialogOpen(false);
      clearComunicazioneSelection();
      fetchComunicazione();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore invio massivo');
    } finally {
      setComunicazioneBulkSending(false);
    }
  };

  const handlePianoRatealeBulkSend = async () => {
    if (pianoRatealeBulkSending) return;
    if (!selectedPianoRatealeIds.length) return;
    if (!pianoRatealeBulkAccountId) {
      toast.error('Seleziona un account mittente');
      return;
    }
    setPianoRatealeBulkSending(true);
    setPianoRatealeBulkProgress({ done: 0, total: selectedPianoRatealeIds.length });
    try {
      const params = normalizeParams(filters);
      for (let i = 0; i < selectedPianoRatealeIds.length; i += 1) {
        const customerId = selectedPianoRatealeIds[i];
        const preview = await api.post(`/negotiation/transazioni-calcolo/piano-rateale/${customerId}/preview`, {}, { params });
        const praticaIds = Array.isArray(preview.data?.pratica_ids) ? preview.data.pratica_ids : [];
        if (!praticaIds.length) {
          setPianoRatealeBulkProgress({ done: i + 1, total: selectedPianoRatealeIds.length });
          continue;
        }
        const rateRows = Array.isArray(preview.data?.rate_rows) ? preview.data.rate_rows : [];
        const tableHtml = buildPianoRatealeTableHtml(rateRows);
        const bodyHtml = `${preview.data?.body_intro_html || ''}${tableHtml}${preview.data?.body_footer_html || ''}`;
        await api.post(
          `/negotiation/transazioni-calcolo/piano-rateale/${customerId}/send`,
          {
            email_account_id: pianoRatealeBulkAccountId,
            to: preview.data?.to_default || '',
            cc: '',
            bcc: '',
            subject: preview.data?.subject || 'Piano rateale transazione',
            body_html: bodyHtml,
            pratica_ids: praticaIds,
            rate_plan: preview.data?.rate_plan || {},
            rate_rows: rateRows
          },
          { params }
        );
        setPianoRatealeBulkProgress({ done: i + 1, total: selectedPianoRatealeIds.length });
      }
      toast.success('Invio completato');
      setPianoRatealeBulkDialogOpen(false);
      clearPianoRatealeSelection();
      fetchPianoRateale();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore invio massivo');
    } finally {
      setPianoRatealeBulkSending(false);
    }
  };

  const openSollecitoBulkDialog = async () => {
    if (!selectedSollecitoIds.length) {
      toast.error('Seleziona almeno un cliente');
      return;
    }
    const accounts = emailAccounts.length ? emailAccounts : await fetchEmailAccounts();
    setSollecitoBulkAccountId((prev) => prev ?? accounts?.[0]?.id ?? null);
    setSollecitoBulkProgress({ done: 0, total: selectedSollecitoIds.length });
    setSollecitoBulkDialogOpen(true);
  };

  const handleSollecitoBulkSend = async () => {
    if (sollecitoBulkSending) return;
    if (!selectedSollecitoIds.length) return;
    if (!sollecitoBulkAccountId) {
      toast.error('Seleziona un account mittente');
      return;
    }
    setSollecitoBulkSending(true);
    setSollecitoBulkProgress({ done: 0, total: selectedSollecitoIds.length });
    try {
      const params = normalizeParams(filters);
      for (let i = 0; i < selectedSollecitoIds.length; i += 1) {
        const customerId = selectedSollecitoIds[i];
        const preview = await api.post(`/negotiation/transazioni-calcolo/sollecito/${customerId}/preview`, {}, { params });
        const praticaIds = Array.isArray(preview.data?.pratica_ids) ? preview.data.pratica_ids : [];
        if (!praticaIds.length) {
          setSollecitoBulkProgress({ done: i + 1, total: selectedSollecitoIds.length });
          continue;
        }
        await api.post(
          `/negotiation/transazioni-calcolo/sollecito/${customerId}/send`,
          {
            email_account_id: sollecitoBulkAccountId,
            to: preview.data?.to_default || '',
            cc: '',
            bcc: '',
            subject: preview.data?.subject || 'Sollecito accordo transazione',
            body_html: preview.data?.body_html || '',
            pratica_ids: praticaIds
          },
          { params }
        );
        setSollecitoBulkProgress({ done: i + 1, total: selectedSollecitoIds.length });
      }
      toast.success('Invio completato');
      setSollecitoBulkDialogOpen(false);
      clearSollecitoSelection();
      fetchSollecito();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore invio massivo');
    } finally {
      setSollecitoBulkSending(false);
    }
  };

  const buildRateRowsFromPlan = (plan) => {
    const total = Number(parseItNumber(plan?.total_amount) || 0);
    const countRaw = Number(plan?.rate_count);
    const count = Number.isFinite(countRaw) ? Math.max(1, Math.min(240, Math.floor(countRaw))) : 10;
    const first = parseItDate(plan?.first_due_date) || new Date();
    const r2 = (n) => Math.round(Number(n || 0) * 100) / 100;
    const base = count > 0 ? r2(total / count) : 0;
    return Array.from({ length: count }).map((_, idx) => {
      const d = new Date(first);
      d.setMonth(d.getMonth() + idx);
      const amount = idx === count - 1 ? r2(total - base * (count - 1)) : base;
      return {
        idx: idx + 1,
        due_date: formatDateItFromDate(d),
        amount: formatNumber2It(amount, '')
      };
    });
  };

  const closePianoRatealeEmailDialog = () => {
    if (pianoRatealeEmailDialogLoading) return;
    setPianoRatealeEmailDialogOpen(false);
    setPianoRatealeEmailTarget(null);
    setPianoRatealeEmailPraticaIds([]);
    setPianoRatealeEmailForm({
      email_account_id: null,
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body_intro_html: '',
      body_footer_html: '',
      rate_plan: {
        total_amount: '',
        rate_count: 10,
        first_due_date: '',
        frequency: 'mensile'
      },
      rate_rows: []
    });
  };

  const openPianoRatealeEmailDialog = async (customerRow) => {
    const customerId = customerRow?.customer_id;
    if (!customerId) return;
    setPianoRatealeEmailDialogOpen(true);
    setPianoRatealeEmailDialogLoading(true);
    setPianoRatealeEmailTarget(customerRow);
    try {
      const accounts = emailAccounts.length ? emailAccounts : await fetchEmailAccounts();
      const params = normalizeParams(filters);
      const res = await api.post(`/negotiation/transazioni-calcolo/piano-rateale/${customerId}/preview`, {}, { params });
      const defaultAccountId = accounts?.[0]?.id ?? null;
      const plan = res.data?.rate_plan || {
        total_amount: '',
        rate_count: 10,
        first_due_date: '',
        frequency: 'mensile'
      };
      const rateRows = Array.isArray(res.data?.rate_rows) ? res.data.rate_rows : buildRateRowsFromPlan(plan);
      setPianoRatealeEmailPraticaIds(Array.isArray(res.data?.pratica_ids) ? res.data.pratica_ids : []);
      setPianoRatealeEmailForm({
        email_account_id: defaultAccountId,
        to: res.data?.to_default || customerRow?.customer_email || '',
        cc: '',
        bcc: '',
        subject: res.data?.subject || '',
        body_intro_html: res.data?.body_intro_html || '',
        body_footer_html: res.data?.body_footer_html || '',
        rate_plan: {
          total_amount: String(plan?.total_amount || ''),
          rate_count: Number(plan?.rate_count || 10) || 10,
          first_due_date: String(plan?.first_due_date || ''),
          frequency: String(plan?.frequency || 'mensile') || 'mensile'
        },
        rate_rows: rateRows
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore preview email');
      closePianoRatealeEmailDialog();
    } finally {
      setPianoRatealeEmailDialogLoading(false);
    }
  };

  const closePianoRatealeViewDialog = () => {
    if (pianoRatealeViewDialogLoading) return;
    setPianoRatealeViewDialogOpen(false);
    setPianoRatealeViewData(null);
  };

  const openPianoRatealeViewDialog = async (customerRow) => {
    const customerId = customerRow?.customer_id;
    if (!customerId) return;
    setPianoRatealeViewDialogOpen(true);
    setPianoRatealeViewDialogLoading(true);
    setPianoRatealeViewData(null);
    try {
      const res = await api.get(`/negotiation/transazioni-calcolo/piano-rateale/${customerId}/latest`);
      setPianoRatealeViewData({ customer: customerRow, ...(res.data || {}) });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore caricamento piano rateale');
      closePianoRatealeViewDialog();
    } finally {
      setPianoRatealeViewDialogLoading(false);
    }
  };

  const updatePianoRatealeRowField = (idx, field, value) => {
    setPianoRatealeEmailForm((prev) => ({
      ...prev,
      rate_rows: (prev.rate_rows || []).map((r) => (Number(r?.idx) === Number(idx) ? { ...r, [field]: value } : r))
    }));
  };

  const regeneratePianoRatealeRows = () => {
    setPianoRatealeEmailForm((prev) => ({
      ...prev,
      rate_rows: buildRateRowsFromPlan(prev.rate_plan)
    }));
  };

  const handleSendPianoRatealeEmail = async () => {
    if (!pianoRatealeEmailTarget?.customer_id) return;
    if (!pianoRatealeEmailForm.email_account_id) {
      toast.error('Seleziona un account mittente');
      return;
    }
    if (!String(pianoRatealeEmailForm.to || '').trim()) {
      toast.error('Destinatario obbligatorio');
      return;
    }
    if (!String(pianoRatealeEmailForm.subject || '').trim()) {
      toast.error('Oggetto obbligatorio');
      return;
    }

    const praticaIds = Array.isArray(pianoRatealeEmailPraticaIds)
      ? pianoRatealeEmailPraticaIds.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
      : [];
    if (!praticaIds.length) {
      toast.error('Nessuna pratica disponibile per piano rateale');
      return;
    }

    const rateRows = Array.isArray(pianoRatealeEmailForm.rate_rows) ? pianoRatealeEmailForm.rate_rows : [];
    if (!rateRows.length) {
      toast.error('Inserisci almeno una rata');
      return;
    }

    const tableHtml = buildPianoRatealeTableHtml(rateRows);
    const bodyHtml = `${pianoRatealeEmailForm.body_intro_html || ''}${tableHtml}${pianoRatealeEmailForm.body_footer_html || ''}`;
    if (!String(bodyHtml || '').trim()) {
      toast.error('Contenuto email obbligatorio');
      return;
    }

    const ratePlan = {
      ...pianoRatealeEmailForm.rate_plan,
      rate_count: Number(pianoRatealeEmailForm.rate_plan?.rate_count || 0) || rateRows.length
    };

    setPianoRatealeEmailDialogLoading(true);
    try {
      const params = normalizeParams(filters);
      await api.post(
        `/negotiation/transazioni-calcolo/piano-rateale/${pianoRatealeEmailTarget.customer_id}/send`,
        {
          email_account_id: pianoRatealeEmailForm.email_account_id,
          to: pianoRatealeEmailForm.to,
          cc: pianoRatealeEmailForm.cc,
          bcc: pianoRatealeEmailForm.bcc,
          subject: pianoRatealeEmailForm.subject,
          body_html: bodyHtml,
          pratica_ids: praticaIds,
          rate_plan: ratePlan,
          rate_rows: rateRows
        },
        { params }
      );
      toast.success('Email inviata');
      closePianoRatealeEmailDialog();
      fetchPianoRateale();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore invio email');
    } finally {
      setPianoRatealeEmailDialogLoading(false);
    }
  };

  const closeSollecitoEmailDialog = () => {
    if (sollecitoEmailDialogLoading) return;
    setSollecitoEmailDialogOpen(false);
    setSollecitoEmailTarget(null);
    setSollecitoEmailForm({
      email_account_id: null,
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body_html: '',
      pratica_ids: []
    });
  };

  const openSollecitoEmailDialog = async (customerRow) => {
    const customerId = customerRow?.customer_id;
    if (!customerId) return;
    setSollecitoEmailDialogOpen(true);
    setSollecitoEmailDialogLoading(true);
    setSollecitoEmailTarget(customerRow);
    try {
      const accounts = emailAccounts.length ? emailAccounts : await fetchEmailAccounts();
      const params = normalizeParams(filters);
      const res = await api.post(`/negotiation/transazioni-calcolo/sollecito/${customerId}/preview`, {}, { params });
      const defaultAccountId = accounts?.[0]?.id ?? null;
      setSollecitoEmailForm({
        email_account_id: defaultAccountId,
        to: res.data?.to_default || customerRow?.customer_email || '',
        cc: '',
        bcc: '',
        subject: res.data?.subject || '',
        body_html: res.data?.body_html || '',
        pratica_ids: Array.isArray(res.data?.pratica_ids) ? res.data.pratica_ids : []
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore preview email');
      setSollecitoEmailTarget(null);
      setSollecitoEmailForm({
        email_account_id: null,
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body_html: '',
        pratica_ids: []
      });
      setSollecitoEmailDialogOpen(false);
    } finally {
      setSollecitoEmailDialogLoading(false);
    }
  };

  const handleSendSollecitoEmail = async () => {
    if (!sollecitoEmailTarget?.customer_id) return;
    if (!sollecitoEmailForm.email_account_id) {
      toast.error('Seleziona un account mittente');
      return;
    }
    if (!String(sollecitoEmailForm.to || '').trim()) {
      toast.error('Destinatario obbligatorio');
      return;
    }
    if (!String(sollecitoEmailForm.subject || '').trim()) {
      toast.error('Oggetto obbligatorio');
      return;
    }
    if (!String(sollecitoEmailForm.body_html || '').trim()) {
      toast.error('Contenuto email obbligatorio');
      return;
    }
    const praticaIds = Array.isArray(sollecitoEmailForm.pratica_ids)
      ? sollecitoEmailForm.pratica_ids.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
      : [];
    if (!praticaIds.length) {
      toast.error('Nessuna pratica da sollecitare');
      return;
    }

    setSollecitoEmailDialogLoading(true);
    try {
      const params = normalizeParams(filters);
      await api.post(
        `/negotiation/transazioni-calcolo/sollecito/${sollecitoEmailTarget.customer_id}/send`,
        {
          email_account_id: sollecitoEmailForm.email_account_id,
          to: sollecitoEmailForm.to,
          cc: sollecitoEmailForm.cc,
          bcc: sollecitoEmailForm.bcc,
          subject: sollecitoEmailForm.subject,
          body_html: sollecitoEmailForm.body_html,
          pratica_ids: praticaIds
        },
        { params }
      );
      toast.success('Email inviata');
      closeSollecitoEmailDialog();
      fetchSollecito();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore invio email');
    } finally {
      setSollecitoEmailDialogLoading(false);
    }
  };

  const toggleEmailRowInclude = (rowId) => {
    setEmailForm((prev) => ({
      ...prev,
      table_rows: (prev.table_rows || []).map((r) => (r?.id === rowId ? { ...r, include: !r.include } : r))
    }));
  };

  const updateEmailRowField = (rowId, field, value) => {
    setEmailForm((prev) => ({
      ...prev,
      table_rows: (prev.table_rows || []).map((r) => (r?.id === rowId ? { ...r, [field]: value } : r))
    }));
  };

  const toggleEmailDettagli = (rowId) => {
    setEmailDettagliExpanded((prev) => ({ ...(prev || {}), [rowId]: !prev?.[rowId] }));
  };

  const getEmailCalcoloData = (r) => {
    const dettagli = r?.calcolo_dettagli_json && typeof r.calcolo_dettagli_json === 'object' ? r.calcolo_dettagli_json : null;
    const ipotesiKey =
      normalizeCalcoloIpotesiKey(dettagli?.ipotesi) || normalizeCalcoloIpotesiKey(r?.calcolo_ipotesi) || '';
    const computed =
      ipotesiKey && dettagli?.inputs && typeof dettagli.inputs === 'object'
        ? computeCalcoloIpotesi(ipotesiKey, dettagli.inputs)
        : null;
    const baseLabel = dettagli?.base?.label || computed?.baseLabel || '';
    const baseValueRaw =
      dettagli?.base?.value != null
        ? Number(dettagli.base.value)
        : computed?.baseValue != null
          ? Number(computed.baseValue)
          : parseItNumber(r?.base_compenso);
    const baseValue = Number.isFinite(baseValueRaw) ? baseValueRaw : null;
    const inputs = dettagli?.inputs && typeof dettagli.inputs === 'object' ? dettagli.inputs : {};
    const outputs = (computed?.outputs && typeof computed.outputs === 'object' ? computed.outputs : dettagli?.outputs) || {};
    return { dettagli, ipotesiKey, baseLabel, baseValue, inputs, outputs };
  };

  const emailTableColSpan =
    2 +
    (emailColumnVisibility.issuer_name ? 1 : 0) +
    (emailColumnVisibility.category_name ? 1 : 0) +
    (emailColumnVisibility.calcolo_ipotesi ? 1 : 0) +
    (emailColumnVisibility.data_chiusura ? 1 : 0) +
    (emailColumnVisibility.importo_chiusura ? 1 : 0) +
    (emailColumnVisibility.importo_accordato ? 1 : 0) +
    (emailColumnVisibility.risparmio ? 1 : 0) +
    (emailColumnVisibility.base_compenso ? 1 : 0) +
    (emailColumnVisibility.perc_transazione ? 1 : 0) +
    (emailColumnVisibility.importo_transazione ? 1 : 0);

  const renderEmailCalcoloDettagli = (r) => {
    const { ipotesiKey, baseLabel, baseValue, inputs, outputs } = getEmailCalcoloData(r);
    const label = getCalcoloIpotesiLabel(ipotesiKey) || (ipotesiKey ? ipotesiKey : '');
    const money = (v, fallback = '') => formatNumber2It(v, fallback);

    const lines = [];
    if (label) lines.push({ k: 'Calcolo', v: label });
    if (baseLabel || baseValue != null)
      lines.push({ k: `Base Compenso${baseLabel ? ` (${baseLabel})` : ''}`, v: baseValue != null ? `${money(baseValue)} €` : '' });
    lines.push({ k: 'Formula', v: 'Importo Transazione = Base Compenso × (Compenso % / 100)' });

    if (ipotesiKey === 'saldo_stralcio') {
      lines.push({ k: 'Importo dovuto', v: inputs.importo_dovuto ? `${money(parseFlexibleNumber(inputs.importo_dovuto))} €` : '' });
      lines.push({ k: 'Importo accordato', v: inputs.importo_accordato ? `${money(parseFlexibleNumber(inputs.importo_accordato))} €` : '' });
      if (outputs?.sconto_percent != null) lines.push({ k: 'Sconto', v: `${money(outputs.sconto_percent)} %` });
      if (outputs?.rata != null) lines.push({ k: 'Rata', v: `${money(outputs.rata)} €` });
    } else if (ipotesiKey === 'carta_revolving') {
      lines.push({ k: 'Saldo debito', v: inputs.saldo_debito ? `${money(parseFlexibleNumber(inputs.saldo_debito))} €` : '' });
      lines.push({
        k: 'Interessi estratto conto',
        v: inputs.interessi_estratto_conto ? `${money(parseFlexibleNumber(inputs.interessi_estratto_conto))} €` : ''
      });
      if (outputs?.capitale_netto_interessi != null) lines.push({ k: 'Capitale netto interessi', v: `${money(outputs.capitale_netto_interessi)} €` });
      if (outputs?.incidenza_interessi_percent != null) lines.push({ k: 'Incidenza interessi', v: `${money(outputs.incidenza_interessi_percent)} %` });
    } else if (ipotesiKey === 'piano_rientro') {
      lines.push({ k: 'Importo erogato', v: inputs.importo_erogato ? `${money(parseFlexibleNumber(inputs.importo_erogato))} €` : '' });
      lines.push({
        k: 'Totale da restituire',
        v: inputs.totale_da_restituire ? `${money(parseFlexibleNumber(inputs.totale_da_restituire))} €` : ''
      });
      if (inputs.numero_rate) lines.push({ k: 'N. rate', v: String(inputs.numero_rate || '') });
      if (inputs.rate_pagate) lines.push({ k: 'Rate pagate', v: String(inputs.rate_pagate || '') });
      if (inputs.data_prima_rata) lines.push({ k: 'Prima rata', v: String(inputs.data_prima_rata || '') });
      if (outputs?.rata != null) lines.push({ k: 'Rata', v: `${money(outputs.rata)} €` });
      if (outputs?.interessi_totali != null) lines.push({ k: 'Interessi totali', v: `${money(outputs.interessi_totali)} €` });
      if (outputs?.interessi_gia_pagati != null) lines.push({ k: 'Interessi già pagati', v: `${money(outputs.interessi_gia_pagati)} €` });
      if (outputs?.interessi_risparmiati != null) lines.push({ k: 'Interessi risparmiati', v: `${money(outputs.interessi_risparmiati)} €` });
    } else if (ipotesiKey === 'finanziamento_decaduto') {
      if (inputs.data_decadenza) lines.push({ k: 'Data decadenza', v: String(inputs.data_decadenza || '') });
      lines.push({
        k: 'Richiesto a decadenza',
        v: inputs.richiesto_a_decadenza ? `${money(parseFlexibleNumber(inputs.richiesto_a_decadenza))} €` : ''
      });
      lines.push({ k: 'Importo accordato', v: inputs.importo_accordato ? `${money(parseFlexibleNumber(inputs.importo_accordato))} €` : '' });
      if (inputs.totale_da_restituire)
        lines.push({ k: 'Totale da restituire', v: `${money(parseFlexibleNumber(inputs.totale_da_restituire))} €` });
      if (inputs.importo_pagato) lines.push({ k: 'Importo pagato', v: `${money(parseFlexibleNumber(inputs.importo_pagato))} €` });
      if (outputs?.rata != null) lines.push({ k: 'Rata', v: `${money(outputs.rata)} €` });
      if (outputs?.debito_residuo_piano != null) lines.push({ k: 'Debito residuo (piano)', v: `${money(outputs.debito_residuo_piano)} €` });
      if (outputs?.interessi_rate_future != null) lines.push({ k: 'Interessi rate future', v: `${money(outputs.interessi_rate_future)} €` });
    }

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 1fr' }, gap: 0.75, fontSize: 12, lineHeight: 1.35 }}>
        {lines
          .filter((x) => String(x?.v || '').trim())
          .map((x, idx) => (
            <Fragment key={`email-det-${r.id}-${idx}`}>
              <Typography variant="caption" sx={{ fontWeight: 900 }}>
                {x.k}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {x.v}
              </Typography>
            </Fragment>
          ))}
      </Box>
    );
  };

  const closeEmailDialog = () => {
    if (emailDialogLoading) return;
    setEmailDialogOpen(false);
    setEmailColumnsAnchorEl(null);
    setEmailTarget(null);
    setEmailDettagliExpanded({});
    setEmailForm({
      email_account_id: null,
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body_intro_html: '',
      body_footer_html: '',
      table_rows: []
    });
  };

  const openEmailDialog = async (customerRow) => {
    const customerId = customerRow?.customer_id;
    if (!customerId) return;
    setEmailDialogOpen(true);
    setEmailDialogLoading(true);
    setEmailTarget(customerRow);
    try {
      const accounts = emailAccounts.length ? emailAccounts : await fetchEmailAccounts();
      const params = normalizeParams(filters);
      const res = await api.post(`/negotiation/transazioni-calcolo/comunicazione/${customerId}/preview`, {}, { params });
      const defaultAccountId = accounts?.[0]?.id ?? null;
      const rows = Array.isArray(res.data?.pratiche)
        ? res.data.pratiche.map((p) => ({
            id: p.id,
            include: true,
            issuer_name: p.issuer_name || '',
            category_name: p.category_name || '',
            calcolo_ipotesi:
              normalizeCalcoloIpotesiKey(p?.calcolo_dettagli_json?.ipotesi) || normalizeCalcoloIpotesiKey(p?.calcolo_ipotesi) || '',
            data_chiusura: formatDate(p.data_chiusura),
            importo_chiusura: formatNumber2It(p.importo_chiusura, ''),
            importo_accordato: formatNumber2It(p.importo_accordato, ''),
            risparmio: formatNumber2It(p.base_calcolo_transazione, ''),
            base_compenso: (() => {
              const ipotesiKey =
                normalizeCalcoloIpotesiKey(p?.calcolo_dettagli_json?.ipotesi) || normalizeCalcoloIpotesiKey(p?.calcolo_ipotesi) || '';
              const computed =
                ipotesiKey && p?.calcolo_dettagli_json?.inputs
                  ? computeCalcoloIpotesi(ipotesiKey, p.calcolo_dettagli_json.inputs)
                  : null;
              const baseValueRaw =
                p?.calcolo_dettagli_json?.base?.value != null
                  ? Number(p.calcolo_dettagli_json.base.value)
                  : computed?.baseValue != null
                    ? Number(computed.baseValue)
                    : null;
              return formatNumber2It(baseValueRaw, '');
            })(),
            perc_transazione: formatNumber2It(p.perc_transazione, ''),
            importo_transazione: formatNumber2It(p.importo_transazione_autorizzata, ''),
            calcolo_dettagli_json: p?.calcolo_dettagli_json ?? null
          }))
        : [];
      setEmailForm({
        email_account_id: defaultAccountId,
        to: res.data?.to_default || customerRow?.customer_email || '',
        cc: '',
        bcc: '',
        subject: res.data?.subject || '',
        body_intro_html: res.data?.body_intro_html || '',
        body_footer_html: res.data?.body_footer_html || '',
        table_rows: rows
      });
      setEmailDettagliExpanded(
        rows.reduce((acc, r) => ({ ...(acc || {}), [r.id]: true }), {})
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore preview email');
      setEmailTarget(null);
      setEmailDettagliExpanded({});
      setEmailForm({
        email_account_id: null,
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body_intro_html: '',
        body_footer_html: '',
        table_rows: []
      });
      setEmailDialogOpen(false);
    } finally {
      setEmailDialogLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTarget?.customer_id) return;
    if (!emailForm.email_account_id) {
      toast.error('Seleziona un account mittente');
      return;
    }
    if (!String(emailForm.to || '').trim()) {
      toast.error('Destinatario obbligatorio');
      return;
    }
    if (!String(emailForm.subject || '').trim()) {
      toast.error('Oggetto obbligatorio');
      return;
    }
    if (!String(emailForm.body_intro_html || '').trim() && !String(emailForm.body_footer_html || '').trim()) {
      toast.error('Contenuto email obbligatorio');
      return;
    }
    const selectedRows = (emailForm.table_rows || []).filter((r) => r?.include);
    if (!selectedRows.length) {
      toast.error('Seleziona almeno una pratica');
      return;
    }

    setEmailDialogLoading(true);
    try {
      const params = normalizeParams(filters);
      const tableHtml = buildComunicazioneTableHtml(selectedRows, emailColumnVisibility);
      const composedBodyHtml = `${emailForm.body_intro_html || ''}${tableHtml}${emailForm.body_footer_html || ''}`;
      await api.post(
        `/negotiation/transazioni-calcolo/comunicazione/${emailTarget.customer_id}/send`,
        {
          email_account_id: emailForm.email_account_id,
          to: emailForm.to,
          cc: emailForm.cc,
          bcc: emailForm.bcc,
          subject: emailForm.subject,
          body_html: composedBodyHtml,
          pratica_ids: selectedRows.map((r) => r.id),
          table_rows: selectedRows,
          table_columns: emailColumnVisibility
        },
        { params }
      );
      toast.success('Email inviata');
      closeEmailDialog();
      fetchComunicazione();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Errore invio email');
    } finally {
      setEmailDialogLoading(false);
    }
  };

  const currentCalcoloInputs = useMemo(
    () => (calcoloInputsByIpotesi && calcoloInputsByIpotesi[calcoloIpotesi] ? calcoloInputsByIpotesi[calcoloIpotesi] : {}),
    [calcoloInputsByIpotesi, calcoloIpotesi]
  );

  const calcoloComputed = useMemo(
    () => computeCalcoloIpotesi(calcoloIpotesi, currentCalcoloInputs),
    [calcoloIpotesi, currentCalcoloInputs]
  );

  const baseCompensoForVerify = useMemo(() => round2(Number(calcoloComputed?.baseValue || 0)), [calcoloComputed?.baseValue]);

  const setCalcoloInput = useCallback(
    (field, value) => {
      setImportoTransazioneAuto(true);
      setCalcoloDirty(true);
      setCalcoloInputsByIpotesi((prev) => ({
        ...(prev || {}),
        [calcoloIpotesi]: { ...((prev || {})[calcoloIpotesi] || {}), [field]: value }
      }));
    },
    [calcoloIpotesi]
  );

  useEffect(() => {
    if (!verifyDialogOpen) return;
    setCalcoloInputsByIpotesi((prev) => {
      if (prev?.[calcoloIpotesi]) return prev;
      return { ...(prev || {}), [calcoloIpotesi]: buildDefaultCalcoloInputs(calcoloIpotesi, verifyRow, verifyForm) };
    });
  }, [verifyDialogOpen, calcoloIpotesi, verifyRow, verifyForm]);

  useEffect(() => {
    if (!verifyDialogOpen) return;
    if (!importoTransazioneAuto) return;
    const perc = Number(verifyForm.perc_transazione || 0);
    const base = Number(baseCompensoForVerify || 0);
    if (!Number.isFinite(perc) || !Number.isFinite(base)) return;
    const computedImporto = round2(base * (perc / 100));
    setVerifyForm((prev) => ({ ...prev, importo_transazione: formatNumber2(computedImporto, prev.importo_transazione) }));
  }, [verifyDialogOpen, importoTransazioneAuto, baseCompensoForVerify, verifyForm.perc_transazione]);

  const calcoloDettagliForSave = useMemo(() => {
    const perc = Number(verifyForm.perc_transazione || 0);
    const importoTransazione = Number(verifyForm.importo_transazione || 0);
    const importoInteressi = verifyForm.importo_interessi === '' ? null : Number(verifyForm.importo_interessi);
    return {
      ipotesi: calcoloIpotesi,
      inputs: currentCalcoloInputs,
      outputs: calcoloComputed?.outputs || {},
      base: { label: calcoloComputed?.baseLabel || '', value: baseCompensoForVerify },
      compenso_percent: Number.isFinite(perc) ? perc : null,
      importo_transazione: Number.isFinite(importoTransazione) ? importoTransazione : null,
      importo_interessi: Number.isFinite(importoInteressi) ? importoInteressi : null
    };
  }, [baseCompensoForVerify, calcoloComputed?.baseLabel, calcoloComputed?.outputs, calcoloIpotesi, currentCalcoloInputs, verifyForm.importo_interessi, verifyForm.importo_transazione, verifyForm.perc_transazione]);

  const openVerifyDialog = (row) => {
    setVerifyRow(row);
    const importoChiusura = formatNumber2(row?.importo_chiusura, '0.00');
    const importoAccordato = formatNumber2(row?.importo_accordato, '0.00');
    const percTransazione = formatNumber2(row?.perc_transazione ?? 10, '10.00');
    const verifyDraft = {
      data_chiusura: row?.data_chiusura ? String(row.data_chiusura).slice(0, 10) : '',
      importo_chiusura: importoChiusura,
      importo_accordato: importoAccordato,
      importo_interessi: formatNumber2(row?.importo_interessi ?? 0, '0.00'),
      perc_transazione: percTransazione,
      importo_transazione: ''
    };

    let savedDettagli = row?.calcolo_dettagli_json ?? null;
    if (savedDettagli && typeof savedDettagli === 'object' && savedDettagli?.type === 'Buffer' && Array.isArray(savedDettagli?.data)) {
      try {
        savedDettagli = JSON.parse(new TextDecoder().decode(new Uint8Array(savedDettagli.data)));
      } catch (_e) {
        savedDettagli = null;
      }
    }
    if (typeof savedDettagli === 'string') {
      try {
        savedDettagli = JSON.parse(savedDettagli);
      } catch (_e) {
        savedDettagli = null;
      }
    }
    const ipotesiFromJson = normalizeCalcoloIpotesiKey(savedDettagli?.ipotesi);
    const ipotesiFromRow = normalizeCalcoloIpotesiKey(row?.calcolo_ipotesi);
    const savedIpotesi = ipotesiFromJson || ipotesiFromRow || 'saldo_stralcio';
    const hasSavedCalcolo = Boolean(ipotesiFromJson) || Boolean(ipotesiFromRow) || Boolean(savedDettagli);
    const savedInputs = savedDettagli?.inputs && typeof savedDettagli.inputs === 'object' ? savedDettagli.inputs : null;
    const defaultInputs = savedInputs || buildDefaultCalcoloInputs(savedIpotesi, row, verifyDraft);
    const computed = computeCalcoloIpotesi(savedIpotesi, defaultInputs);
    const base = round2(Number(computed?.baseValue || 0));
    const computedImporto = round2(base * (Number(percTransazione || 0) / 100));

    setCalcoloIpotesi(savedIpotesi);
    setCalcoloIpotesiSaved(hasSavedCalcolo ? savedIpotesi : null);
    setCalcoloInputsByIpotesi({ [savedIpotesi]: defaultInputs });
    setImportoTransazioneAuto(true);
    setCalcoloDirty(false);
    setCalcoloIpotesiChangeDialogOpen(false);
    setPendingCalcoloIpotesi(null);

    setVerifyForm({
      ...verifyDraft,
      importo_transazione: formatNumber2(row?.importo_transazione ?? computedImporto, '0.00')
    });
    setVerifyDialogOpen(true);
  };

  const closeVerifyDialog = () => {
    if (verifyLoading) return;
    setUnverifyForceDialogOpen(false);
    setUnverifyBlockInfo(null);
    setVerifyDialogOpen(false);
    setVerifyRow(null);
    setCalcoloIpotesi('saldo_stralcio');
    setCalcoloIpotesiSaved(null);
    setCalcoloInputsByIpotesi({});
    setImportoTransazioneAuto(true);
    setCalcoloDirty(false);
    setCalcoloIpotesiChangeDialogOpen(false);
    setPendingCalcoloIpotesi(null);
  };

  const applyCalcoloIpotesiChange = useCallback(
    (next) => {
      if (!next) return;
      setCalcoloIpotesi(next);
      setCalcoloInputsByIpotesi({ [next]: buildDefaultCalcoloInputs(next, verifyRow, verifyForm) });
      setImportoTransazioneAuto(true);
      setCalcoloDirty(false);
    },
    [verifyRow, verifyForm]
  );

  const handleVerifyConfirm = async () => {
    if (!verifyRow?.id) return;
    const importoChiusura = Number(verifyForm.importo_chiusura);
    const perc = Number(verifyForm.perc_transazione);
    const importoTransazione = Number(verifyForm.importo_transazione);
    if (!verifyForm.data_chiusura) {
      toast.error('Data chiusura obbligatoria');
      return;
    }
    if (Number.isNaN(importoChiusura)) {
      toast.error('Importo chiusura non valido');
      return;
    }
    if (Number.isNaN(perc)) {
      toast.error('Percentuale transazione non valida');
      return;
    }
    if (Number.isNaN(importoTransazione)) {
      toast.error('Importo transazione non valido');
      return;
    }

    setVerifyLoading(true);
    try {
      await api.post(`/negotiation/transazioni-calcolo/${verifyRow.id}/verify`, {
        data_chiusura: verifyForm.data_chiusura,
        importo_chiusura: importoChiusura,
        importo_accordato: verifyForm.importo_accordato === '' ? null : Number(verifyForm.importo_accordato),
        importo_interessi: verifyForm.importo_interessi === '' ? null : Number(verifyForm.importo_interessi),
        perc_transazione: perc,
        importo_transazione: importoTransazione,
        calcolo_ipotesi: calcoloIpotesi,
        calcolo_dettagli_json: calcoloDettagliForSave
      });
      toast.success('Transazione salvata');
      setVerifyDialogOpen(false);
      setVerifyRow(null);
      fetchData();
    } catch (err) {
      toast.error('Errore aggiornamento stato');
    } finally {
      setVerifyLoading(false);
    }
  };

  const attemptUnverify = async (force) => {
    if (!verifyRow?.id) return;
    setVerifyLoading(true);
    try {
      await api.post(`/negotiation/transazioni-calcolo/${verifyRow.id}/unverify`, force ? { force: true } : {});
      toast.success(force ? 'Transazione annullata (forzata)' : 'Transazione annullata');
      setUnverifyForceDialogOpen(false);
      setUnverifyBlockInfo(null);
      setVerifyDialogOpen(false);
      setVerifyRow(null);
      fetchData();
    } catch (err) {
      if (!force && err?.response?.status === 409) {
        const payload = err?.response?.data || null;
        setUnverifyBlockInfo({
          ...(payload || {}),
          isBlocking: true
        });
        setUnverifyForceDialogOpen(true);
      } else {
        toast.error(err?.response?.data?.message || 'Errore annullamento transazione');
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleUnverifyConfirm = async () => {
    if (!verifyRow?.id) return;
    setVerifyLoading(true);
    try {
      const depsRes = await api.get(`/negotiation/transazioni-calcolo/${verifyRow.id}/unverify-dependencies`);
      const invii = Array.isArray(depsRes?.data?.invii) ? depsRes.data.invii : [];
      if (invii.length > 0) {
        const isBlocking = invii.some((r) => String(r?.fase || '') !== 'comunicazione');
        setUnverifyBlockInfo({
          message: isBlocking
            ? 'Impossibile annullare: esistono invii collegati (Sollecito e/o Piano rateale). Per procedere serve la forza annullamento.'
            : 'Esiste una Comunicazione collegata. Puoi annullare il calcolo mantenendo lo storico invii, oppure usare la forza annullamento per eliminare anche la comunicazione.',
          dependencies: { invii },
          isBlocking
        });
        setUnverifyForceDialogOpen(true);
        return;
      }
    } catch (_e) {
    } finally {
      setVerifyLoading(false);
    }

    await attemptUnverify(false);
  };

  return (
    <Box sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(37, 99, 235, 0.10)',
              color: '#2563eb'
            }}
          >
            <Calculator size={18} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Transazioni (Calcolo)
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '1rem' }}>
              Lista pratiche con calcolo transazione e stato verifica
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          onClick={startWorkflowReportJob}
          disabled={workflowReportLoading}
          startIcon={<Download size={18} />}
        >
          Report
        </Button>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <StatWidget
                title="Pratiche confermate"
                value={`${Number(stats?.totalePraticheVerificate || 0).toLocaleString('it-IT')}/${Number(stats?.totalePraticheAll ?? stats?.totalePratiche ?? 0).toLocaleString('it-IT')}`}
                icon={WalletCards}
                color="#334155"
                subtitle={`${(() => {
                  const totalAll = Number(stats?.totalePraticheAll ?? stats?.totalePratiche ?? 0);
                  const pct = totalAll > 0 ? Number(stats?.percentualePraticheVerificate ?? 0) : 0;
                  const decimals = Math.abs(pct - Math.round(pct)) < 1e-9 ? 0 : 2;
                  return `${formatPercentIt(pct, decimals)} del totale pratiche`;
                })()}`}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatWidget
                title="Totale Risparmio"
                value={formatMoney(stats?.totaleRisparmio || 0)}
                icon={TrendingUp}
                color="#059669"
                subtitle="Totale delle somme Risparmiate"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatWidget
                title="Totale Compenso dovuto"
                value={formatMoney(stats?.totaleCompensoDovuto || 0)}
                icon={Calculator}
                color="#d97706"
                subtitle="Compenso sulle Transazioni"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatWidget
                title="Totale Compenso autorizzato"
                value={formatMoney(stats?.totaleCompensoAutorizzato || 0)}
                icon={BadgePercent}
                color="#dc2626"
                subtitle="Compenso autorizzato sulle Transazioni"
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between'
          }}
        >
          <TextField
            size="small"
            placeholder="Cerca"
            value={filters.search}
            onChange={(e) => {
              setPage(0);
              setFilters((prev) => ({ ...prev, search: e.target.value }));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="currentColor" />
                </InputAdornment>
              )
            }}
            sx={(theme) => ({
              minWidth: 360,
              '& .MuiInputAdornment-root': { color: theme.palette.text.secondary },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.42) : '#ffffff',
                fontSize: '1rem',
                color: theme.palette.text.primary,
                '& fieldset': { borderColor: theme.palette.divider },
                '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
              }
            })}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Mostra filtri avanzati">
              <IconButton
                size="small"
                onClick={() => setShowAdvanced((prev) => !prev)}
                sx={{ color: 'text.secondary' }}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Seleziona colonne">
              <IconButton
                size="small"
                onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
                sx={{ color: 'text.secondary' }}
              >
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={columnsAnchorEl}
            open={Boolean(columnsAnchorEl)}
            onClose={() => setColumnsAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, pt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Colonne visibili
              </Typography>
            </Box>
            <FormGroup sx={{ px: 2, pb: 1 }}>
              <FormControlLabel control={<Checkbox checked={columnVisibility.responsabileLegale} onChange={() => setColumnVisibility((prev) => ({ ...prev, responsabileLegale: !prev.responsabileLegale }))} />} label="Resp. legale" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.responsabileContabile} onChange={() => setColumnVisibility((prev) => ({ ...prev, responsabileContabile: !prev.responsabileContabile }))} />} label="Resp. contabile" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.dataAssegnazione} onChange={() => setColumnVisibility((prev) => ({ ...prev, dataAssegnazione: !prev.dataAssegnazione }))} />} label="Data assegnazione" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.cliente} onChange={() => setColumnVisibility((prev) => ({ ...prev, cliente: !prev.cliente }))} />} label="Cliente" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.cluster} onChange={() => setColumnVisibility((prev) => ({ ...prev, cluster: !prev.cluster }))} />} label="Cluster" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.praticheDaChiudere} onChange={() => setColumnVisibility((prev) => ({ ...prev, praticheDaChiudere: !prev.praticheDaChiudere }))} />} label="Pratiche da chiudere" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.ultimaFattura} onChange={() => setColumnVisibility((prev) => ({ ...prev, ultimaFattura: !prev.ultimaFattura }))} />} label="Ultima fattura" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.contrattiImporto} onChange={() => setColumnVisibility((prev) => ({ ...prev, contrattiImporto: !prev.contrattiImporto }))} />} label="Contratti" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.upsellImporto} onChange={() => setColumnVisibility((prev) => ({ ...prev, upsellImporto: !prev.upsellImporto }))} />} label="Upsell" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.incassatoImporto} onChange={() => setColumnVisibility((prev) => ({ ...prev, incassatoImporto: !prev.incassatoImporto }))} />} label="Incassato" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.incassatoPercent} onChange={() => setColumnVisibility((prev) => ({ ...prev, incassatoPercent: !prev.incassatoPercent }))} />} label="% sul totale" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.controparte} onChange={() => setColumnVisibility((prev) => ({ ...prev, controparte: !prev.controparte }))} />} label="Controparte" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.tipologia} onChange={() => setColumnVisibility((prev) => ({ ...prev, tipologia: !prev.tipologia }))} />} label="Tipologia" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.statoAttuale} onChange={() => setColumnVisibility((prev) => ({ ...prev, statoAttuale: !prev.statoAttuale }))} />} label="Stato Transazione" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.dataChiusura} onChange={() => setColumnVisibility((prev) => ({ ...prev, dataChiusura: !prev.dataChiusura }))} />} label="Data chiusura" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.giorniChiusura} onChange={() => setColumnVisibility((prev) => ({ ...prev, giorniChiusura: !prev.giorniChiusura }))} />} label="GG chiusura" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.importoChiusura} onChange={() => setColumnVisibility((prev) => ({ ...prev, importoChiusura: !prev.importoChiusura }))} />} label="Importo chiusura" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.importoAccordato} onChange={() => setColumnVisibility((prev) => ({ ...prev, importoAccordato: !prev.importoAccordato }))} />} label="Importo accordato" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.percentualeRisparmio} onChange={() => setColumnVisibility((prev) => ({ ...prev, percentualeRisparmio: !prev.percentualeRisparmio }))} />} label="% risparmio" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.modalitaChiusura} onChange={() => setColumnVisibility((prev) => ({ ...prev, modalitaChiusura: !prev.modalitaChiusura }))} />} label="Modalità chiusura" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.baseCalcoloTransazione} onChange={() => setColumnVisibility((prev) => ({ ...prev, baseCalcoloTransazione: !prev.baseCalcoloTransazione }))} />} label="Base calcolo" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.percTransazione} onChange={() => setColumnVisibility((prev) => ({ ...prev, percTransazione: !prev.percTransazione }))} />} label="% transaz." />
              <FormControlLabel control={<Checkbox checked={columnVisibility.importoTransazioneCalcolata} onChange={() => setColumnVisibility((prev) => ({ ...prev, importoTransazioneCalcolata: !prev.importoTransazioneCalcolata }))} />} label="Importo calcolato" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.percTransazioneApplicata} onChange={() => setColumnVisibility((prev) => ({ ...prev, percTransazioneApplicata: !prev.percTransazioneApplicata }))} />} label="% applicata" />
              <FormControlLabel control={<Checkbox checked={columnVisibility.importoTransazioneAutorizzata} onChange={() => setColumnVisibility((prev) => ({ ...prev, importoTransazioneAutorizzata: !prev.importoTransazioneAutorizzata }))} />} label="Importo autorizz." />
            </FormGroup>
          </Menu>
        </Box>

        {showAdvanced && (
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.responsabiliLegali}
                  value={filters.responsabileLegale}
                  onChange={(_e, value) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, responsabileLegale: value }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. legale" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  disabled={!isAdminUser}
                  options={isAdminUser ? filterOptions.responsabiliContabili : contabileFullName ? [contabileFullName] : []}
                  value={filters.responsabileContabile}
                  onChange={(_e, value) => {
                    if (!isAdminUser) return;
                    setPage(0);
                    setFilters((prev) => ({ ...prev, responsabileContabile: value }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. contabile" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.controparti}
                  value={filters.controparte}
                  onChange={(_e, value) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, controparte: value }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Controparte" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.tipologie}
                  value={filters.tipologia}
                  onChange={(_e, value) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, tipologia: value }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Tipologia" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.clusters}
                  value={filters.cluster}
                  onChange={(_e, value) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, cluster: value }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Cluster" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  size="small"
                  options={ULTIMA_FATTURA_AGE_OPTIONS}
                  value={ULTIMA_FATTURA_AGE_OPTIONS.find((o) => o.value === filters.ultimaFatturaAge) || null}
                  onChange={(_e, option) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, ultimaFatturaAge: option?.value || '' }));
                  }}
                  getOptionLabel={(o) => o?.label || ''}
                  renderInput={(params) => <TextField {...params} label="Ultima fattura" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.statuses}
                  value={filters.status}
                  onChange={(_e, value) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, status: value }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Stato" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.modalitaChiusura}
                  value={filters.modalitaChiusura}
                  onChange={(_e, value) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, modalitaChiusura: value }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Modalità chiusura" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.statoVerifica}
                  value={filters.statoVerifica}
                  onChange={(_e, value) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, statoVerifica: value }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Stato verifica" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  size="small"
                  options={IMPORTO_ACCORDATO_FILTER_OPTIONS}
                  getOptionLabel={(o) => o.label}
                  value={IMPORTO_ACCORDATO_FILTER_OPTIONS.find((o) => o.value === filters.importoAccordatoFilter) || null}
                  onChange={(_e, value) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, importoAccordatoFilter: value?.value || '' }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Importo accordato" />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>% sul totale</InputLabel>
                  <Select
                    value={filters.incassatoPercentRange}
                    label="% sul totale"
                    onChange={(event) => {
                      setPage(0);
                      setFilters((prev) => ({ ...prev, incassatoPercentRange: event.target.value }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Tutte</em>
                    </MenuItem>
                    {INCASSATO_PERCENT_RANGE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  label="Chiusura da"
                  type="date"
                  value={filters.dataChiusuraFrom || ''}
                  onChange={(e) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, dataChiusuraFrom: e.target.value }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  label="Chiusura a"
                  type="date"
                  value={filters.dataChiusuraTo || ''}
                  onChange={(e) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, dataChiusuraTo: e.target.value }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  label="Assegnazione da"
                  type="date"
                  value={filters.dataAssegnazioneFrom || ''}
                  onChange={(e) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, dataAssegnazioneFrom: e.target.value }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  label="Assegnazione a"
                  type="date"
                  value={filters.dataAssegnazioneTo || ''}
                  onChange={(e) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, dataAssegnazioneTo: e.target.value }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {activeFilterChips.length > 0 && (
          <Box
            sx={{
              px: 3,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 0.5 }}>
              Filtri attivi
            </Typography>
            {activeFilterChips.map((chip) => (
              <Chip
                key={chip.key}
                size="small"
                label={chip.label}
                onDelete={chip.locked ? undefined : () => handleDeleteActiveFilter(chip.key)}
                sx={(theme) => ({
                  fontWeight: 600,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.24)' : '#F3F4F6',
                  color: theme.palette.text.primary,
                  border: '1px solid',
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.45)'
                      : 'rgba(148, 163, 184, 0.35)'
                })}
              />
            ))}
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setPage(0);
                setFilters(getDefaultFilters());
              }}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Reset
            </Button>
          </Box>
        )}

        <Box sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs value={viewTab} onChange={(_event, nextTab) => setViewTab(nextTab)} textColor="primary" indicatorColor="primary">
            <Tab value="list" label="Calcolo" />
            <Tab value="comunicazione" label="Comunicazione" />
            <Tab value="sollecito" label="Sollecito" />
            <Tab value="piano_rateale" label="Piano rateale" />
          </Tabs>
        </Box>

        {viewTab === 'list' ? (
          <>
            <TableContainer>
              <Table
                size="small"
                sx={(theme) => ({
                  '& .MuiTableCell-root': {
                    borderColor: 'divider',
                    color: theme.palette.text.primary
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }
                })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }} />
                    {columnVisibility.responsabileLegale && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'responsabile_legale'} direction={orderBy === 'responsabile_legale' ? order : 'asc'} onClick={() => handleRequestSort('responsabile_legale')}>
                          {twoLineHeader('Referente', 'Legale')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {columnVisibility.responsabileContabile && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'responsabile_contabile'} direction={orderBy === 'responsabile_contabile' ? order : 'asc'} onClick={() => handleRequestSort('responsabile_contabile')}>
                          {twoLineHeader('Referente', 'Contabile')}
                        </TableSortLabel>
                      </TableCell>
                    )}
                {columnVisibility.dataAssegnazione && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'data_assegnazione_legale'} direction={orderBy === 'data_assegnazione_legale' ? order : 'asc'} onClick={() => handleRequestSort('data_assegnazione_legale')}>
                      {twoLineHeader('Data assegn.', 'legale')}
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.cliente && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'cliente'} direction={orderBy === 'cliente' ? order : 'asc'} onClick={() => handleRequestSort('cliente')}>
                      Cliente
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.cluster && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'cluster'} direction={orderBy === 'cluster' ? order : 'asc'} onClick={() => handleRequestSort('cluster')}>
                      Cluster
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.praticheDaChiudere && (
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'pratiche_da_chiudere'}
                      direction={orderBy === 'pratiche_da_chiudere' ? order : 'asc'}
                      onClick={() => handleRequestSort('pratiche_da_chiudere')}
                    >
                      {twoLineHeader('Pratiche', 'da chiudere')}
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.ultimaFattura && <TableCell>{twoLineHeader('Ultima', 'fattura')}</TableCell>}
                {columnVisibility.contrattiImporto && <TableCell align="right">Contratti</TableCell>}
                {columnVisibility.upsellImporto && <TableCell align="right">Upsell</TableCell>}
                {columnVisibility.incassatoImporto && <TableCell align="right">Incassato</TableCell>}
                {columnVisibility.incassatoPercent && <TableCell align="center">{twoLineHeader('% sul', 'totale')}</TableCell>}
                {columnVisibility.controparte && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'controparte'} direction={orderBy === 'controparte' ? order : 'asc'} onClick={() => handleRequestSort('controparte')}>
                      Controparte
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.tipologia && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'tipologia'} direction={orderBy === 'tipologia' ? order : 'asc'} onClick={() => handleRequestSort('tipologia')}>
                      Tipologia
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.statoAttuale && <TableCell>Stato Transazione</TableCell>}
                {columnVisibility.dataChiusura && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'data_chiusura'} direction={orderBy === 'data_chiusura' ? order : 'asc'} onClick={() => handleRequestSort('data_chiusura')}>
                      {twoLineHeader('Data', 'chiusura')}
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.giorniChiusura && (
                  <TableCell align="right">
                    <TableSortLabel active={orderBy === 'giorni_chiusura'} direction={orderBy === 'giorni_chiusura' ? order : 'asc'} onClick={() => handleRequestSort('giorni_chiusura')}>
                      {twoLineHeader('GG', 'chiusura')}
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.importoChiusura && (
                  <TableCell align="right">
                    <TableSortLabel active={orderBy === 'importo_chiusura'} direction={orderBy === 'importo_chiusura' ? order : 'asc'} onClick={() => handleRequestSort('importo_chiusura')}>
                      {twoLineHeader('Importo', 'chiusura')}
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.importoAccordato && (
                  <TableCell align="right">
                    <TableSortLabel active={orderBy === 'importo_accordato'} direction={orderBy === 'importo_accordato' ? order : 'asc'} onClick={() => handleRequestSort('importo_accordato')}>
                      {twoLineHeader('Importo', 'accordato')}
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.percentualeRisparmio && (
                  <TableCell align="right">
                    <TableSortLabel active={orderBy === 'percentuale_risparmio'} direction={orderBy === 'percentuale_risparmio' ? order : 'asc'} onClick={() => handleRequestSort('percentuale_risparmio')}>
                      {twoLineHeader('%', 'Risparmio')}
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.modalitaChiusura && (
                  <TableCell>
                    <TableSortLabel active={orderBy === 'modalita_chiusura'} direction={orderBy === 'modalita_chiusura' ? order : 'asc'} onClick={() => handleRequestSort('modalita_chiusura')}>
                      {twoLineHeader('Modalità', 'chiusura')}
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.baseCalcoloTransazione && <TableCell align="right">{twoLineHeader('Base per', 'calcolo Transaz.')}</TableCell>}
                {columnVisibility.percTransazione && <TableCell align="right">{twoLineHeader('%', 'Transaz.')}</TableCell>}
                {columnVisibility.importoTransazioneCalcolata && <TableCell align="right">{twoLineHeader('Importo Transaz.', '(Calcolata)')}</TableCell>}
                {columnVisibility.percTransazioneApplicata && <TableCell align="right">{twoLineHeader('% Transaz.', '(applicata)')}</TableCell>}
                {columnVisibility.importoTransazioneAutorizzata && <TableCell align="right">{twoLineHeader('Importo Transaz.', '(autorizzata)')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
                        <Box sx={{ position: 'sticky', left: 0, width: '100vw', display: 'flex', justifyContent: 'center' }}>
                          <LoaderProgress size={48} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={totalColumns} align="center" sx={{ py: 3 }}>
                        <Box sx={{ position: 'sticky', left: 0, width: '100vw', display: 'flex', justifyContent: 'center' }}>
                          Nessuna pratica trovata
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => navigate(`/negotiation/${row.id}`)}
                        sx={(theme) => ({ cursor: 'pointer', '&:hover td': { bgcolor: theme.palette.action.hover } })}
                      >
                        <TableCell
                          align="center"
                          sx={{ width: 56 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openVerifyDialog(row);
                          }}
                        >
                          {row.stato_movimento === 'verificato' ? (
                            <Tooltip title="Verificato">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ThumbsUp size={22} color="#16a34a" />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Da verificare">
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'rgba(245, 158, 11, 0.12)',
                                  border: '1px solid rgba(245, 158, 11, 0.35)'
                                }}
                              >
                                <HelpCircle size={18} color="#f59e0b" />
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                    {columnVisibility.responsabileLegale && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <User size={15} color="currentColor" />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.responsabile_legale_name || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {columnVisibility.responsabileContabile && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <User size={15} color="currentColor" />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.responsabile_contabile_name || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {columnVisibility.dataAssegnazione && <TableCell>{formatDate(row.data_assegnazione_legale)}</TableCell>}
                    {columnVisibility.cliente && (
                      <TableCell>
                        <Box sx={{ minWidth: 260, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(37, 99, 235, 0.08)',
                              color: '#2563eb',
                              mt: 0.1,
                              flexShrink: 0
                            }}
                          >
                            <User size={16} />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.15 }}>
                              {getCustomerDisplayName(row)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.15 }}>
                              <Phone size={12} color="#64748b" />
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                                {row.customer_telephone || '-'}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.15, display: 'block', lineHeight: 1.15 }}>
                              {row.customer_email || '-'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    )}
                    {columnVisibility.cluster && (
                      <TableCell>
                        {row.cluster_label ? (
                          <Chip
                            size="small"
                            label={row.cluster_label}
                            sx={(theme) => {
                              const tone = getClusterTone(theme, row.cluster_label);
                              return {
                                height: 22,
                                borderRadius: 999,
                                fontWeight: 800,
                                bgcolor: alpha(tone, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                color: tone,
                                border: '1px solid',
                                borderColor: alpha(tone, theme.palette.mode === 'dark' ? 0.35 : 0.28)
                              };
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.praticheDaChiudere && (
                      <TableCell align="right">
                        {Number.isFinite(Number(row.open_pratiche_count)) ? Number(row.open_pratiche_count) : 0}
                      </TableCell>
                    )}
                    {columnVisibility.ultimaFattura && (
                      <TableCell>
                        {row.ultima_fattura_serie_numero ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                            <Typography variant="caption" sx={{ fontWeight: 400, color: 'text.secondary', lineHeight: 1.15 }}>
                              {row.ultima_fattura_serie_numero}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={(theme) => ({
                                fontWeight: 800,
                                color: getInvoiceAgeColor(theme, row.ultima_fattura_data),
                                lineHeight: 1.15
                              })}
                            >
                              {formatDate(row.ultima_fattura_data)}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 400, color: 'text.secondary', lineHeight: 1.15 }}>
                              {row.ultima_fattura_importo == null ? '-' : formatMoney(row.ultima_fattura_importo)}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.contrattiImporto && <TableCell align="right">{formatMoney(row.contratti_importo)}</TableCell>}
                    {columnVisibility.upsellImporto && <TableCell align="right">{formatMoney(row.upsell_importo)}</TableCell>}
                    {columnVisibility.incassatoImporto && <TableCell align="right">{formatMoney(row.incassato_importo)}</TableCell>}
                    {columnVisibility.incassatoPercent && (
                      <TableCell align="center">
                        <IncassatoPercentRing value={row.incassato_percent} />
                      </TableCell>
                    )}
                    {columnVisibility.controparte && <TableCell>{row.issuer_name || '-'}</TableCell>}
                    {columnVisibility.tipologia && <TableCell>{row.category_name || '-'}</TableCell>}
                    {columnVisibility.statoAttuale && (
                      <TableCell>
                        {(() => {
                          const stato = getCalcoloStatoAttuale(row);
                          const tooltip =
                            stato.key === 'piano_rateale_inviato'
                              ? `Piano rateale inviato il ${formatDate(row.last_piano_rateale_il)}`
                              : stato.key === 'sollecito_inviato'
                                ? `Sollecito inviato il ${formatDate(row.last_sollecito_il)}`
                                : stato.key === 'comunicazione_inviata'
                                  ? `Comunicazione inviata il ${formatDate(row.last_comunicazione_il)}`
                                  : stato.key === 'da_inviare_comunicazione'
                                    ? 'Transazione verificata: comunicazione non inviata'
                                    : 'Transazione da verificare';

                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                              <Tooltip title={tooltip}>
                                <Chip
                                  size="small"
                                  label={stato.label}
                                  sx={(theme) => {
                                    const tone =
                                      stato.key === 'piano_rateale_inviato'
                                        ? theme.palette.success.main
                                        : stato.key === 'sollecito_inviato'
                                          ? theme.palette.secondary.main
                                          : stato.key === 'comunicazione_inviata'
                                            ? theme.palette.info.main
                                            : stato.key === 'da_inviare_comunicazione'
                                              ? theme.palette.error.main
                                              : theme.palette.warning.main;
                                    return {
                                      height: 22,
                                      borderRadius: 999,
                                      fontWeight: 800,
                                      bgcolor: alpha(tone, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                      color: tone,
                                      border: '1px solid',
                                      borderColor: alpha(tone, 0.45),
                                      '& .MuiChip-label': { px: 1 }
                                    };
                                  }}
                                />
                              </Tooltip>
                              <WorkflowProgressRing row={row} />
                            </Box>
                          );
                        })()}
                      </TableCell>
                    )}
                    {columnVisibility.dataChiusura && <TableCell>{formatDate(row.data_chiusura)}</TableCell>}
                    {columnVisibility.giorniChiusura && (
                      <TableCell align="right">
                        {row.giorni_chiusura == null ? (
                          '-'
                        ) : (
                          <Chip
                            size="small"
                            label={`${Number(row.giorni_chiusura)} gg`}
                            sx={(theme) => {
                              const giorni = Number(row.giorni_chiusura || 0);
                              const tone =
                                giorni <= 30
                                  ? theme.palette.success.main
                                  : giorni <= 60
                                    ? theme.palette.warning.main
                                    : theme.palette.error.main;
                              return {
                                height: 22,
                                borderRadius: 999,
                                fontWeight: 700,
                                bgcolor: alpha(tone, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                color: tone,
                                border: '1px solid',
                                borderColor: alpha(tone, 0.45),
                                '& .MuiChip-label': { px: 1 }
                              };
                            }}
                          />
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.importoChiusura && (
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                          {formatMoney(row.importo_chiusura)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.importoAccordato && (
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>
                          {formatMoney(row.importo_accordato)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.percentualeRisparmio && (
                      <TableCell align="right" sx={{ fontWeight: 700, color: Number(row.percentuale_risparmio || 0) >= 0 ? '#059669' : '#dc2626' }}>
                        {formatPercent(row.percentuale_risparmio, 1)}
                      </TableCell>
                    )}
                    {columnVisibility.modalitaChiusura && <TableCell>{row.modalita_chiusura || '-'}</TableCell>}
                    {columnVisibility.baseCalcoloTransazione && (
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatMoney(row.base_calcolo_transazione)}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.percTransazione && (
                      <TableCell align="right">
                        {row.perc_transazione == null ? (
                          '-'
                        ) : (
                          <Chip size="small" label={`${Number(row.perc_transazione)}`} sx={{ height: 22, borderRadius: 999, fontWeight: 700 }} />
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.importoTransazioneCalcolata && (
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={formatMoney(row.importo_transazione_calcolata)}
                          sx={{ height: 22, borderRadius: 999, fontWeight: 700 }}
                        />
                      </TableCell>
                    )}
                    {columnVisibility.percTransazioneApplicata && (
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#d97706' }}>
                        {row.perc_transazione == null ? '-' : formatPercent(row.perc_transazione, 2)}
                      </TableCell>
                    )}
                    {columnVisibility.importoTransazioneAutorizzata && (
                      <TableCell align="right">
                        {row.importo_transazione_autorizzata == null ? (
                          '-'
                        ) : (
                          <Chip
                            size="small"
                            label={formatMoney(row.importo_transazione_autorizzata)}
                            sx={(theme) => ({
                              height: 22,
                              borderRadius: 999,
                              fontWeight: 800,
                              bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                              color: theme.palette.success.main,
                              border: '1px solid',
                              borderColor: alpha(theme.palette.success.main, 0.45)
                            })}
                          />
                        )}
                      </TableCell>
                    )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Righe per pagina"
            />
          </>
        ) : viewTab === 'comunicazione' ? (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2, flexWrap: 'nowrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                  Comunicazioni (Transazioni)
                </Typography>
                <Tooltip title="Mostra anche le pratiche per cui la comunicazione è già stata inviata (solo consultazione).">
                  <span>
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      <HelpCircle size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Button
                  variant={comunicazioneShowSent ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    clearComunicazioneSelection();
                    setComunicazioneExpanded({});
                    setComunicazionePage(0);
                    setComunicazioneShowSent((prev) => !prev);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    ...(comunicazioneShowSent
                      ? { bgcolor: 'rgb(33, 43, 54)', '&:hover': { bgcolor: 'rgb(22, 28, 36)' } }
                      : {})
                  }}
                >
                  {comunicazioneShowSent ? 'Già inviate: ON' : 'Mostra già inviate'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => setComunicazioneSelectAllAnchorEl(e.currentTarget)}
                  startIcon={<ViewColumnIcon fontSize="small" />}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  Seleziona
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={openComunicazioneBulkDialogForAllFiltered}
                  disabled={comunicazioneLoading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 900,
                    bgcolor: 'rgb(33, 43, 54)',
                    '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
                  }}
                >
                  Invia tutti
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={openComunicazioneBulkDialog}
                  disabled={!selectedComunicazioneIds.length || comunicazioneLoading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 900,
                    bgcolor: 'rgb(33, 43, 54)',
                    '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
                  }}
                >
                  Invia selezionati ({selectedComunicazioneIds.length})
                </Button>
              </Box>
            </Box>

            <Menu
              anchorEl={comunicazioneSelectAllAnchorEl}
              open={Boolean(comunicazioneSelectAllAnchorEl)}
              onClose={() => setComunicazioneSelectAllAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  setComunicazioneSelectedForPage(true);
                  setComunicazioneSelectAllAnchorEl(null);
                }}
              >
                Seleziona pagina
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setComunicazioneSelectedForPage(false);
                  setComunicazioneSelectAllAnchorEl(null);
                }}
              >
                Deseleziona pagina
              </MenuItem>
              <MenuItem onClick={selectAllComunicazioneFiltered}>Seleziona tutti filtrati</MenuItem>
              <MenuItem
                onClick={() => {
                  clearComunicazioneSelection();
                  setComunicazioneSelectAllAnchorEl(null);
                }}
              >
                Pulisci selezione
              </MenuItem>
            </Menu>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={comunicazioneSelectionState.all}
                        indeterminate={comunicazioneSelectionState.some}
                        onChange={(e) => setComunicazioneSelectedForPage(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 56 }} />
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Nuove pratiche</TableCell>
                    <TableCell align="right">Totale importo</TableCell>
                    <TableCell>Ultimo invio</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comunicazioneLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Box sx={{ position: 'sticky', left: 0, width: '100vw', display: 'flex', justifyContent: 'center' }}>
                          <LoaderProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : comunicazioneRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                        <Box sx={{ position: 'sticky', left: 0, width: '100vw', display: 'flex', justifyContent: 'center' }}>
                          {comunicazioneShowSent
                            ? 'Nessuna comunicazione disponibile con i filtri selezionati'
                            : 'Nessuna comunicazione da inviare con i filtri selezionati'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    comunicazioneRows.map((c) => {
                      const expanded = Boolean(comunicazioneExpanded[String(c.customer_id)]);
                      const checked = Boolean(comunicazioneSelected?.[String(c.customer_id)]);
                      const sendableCount = Number(c.pending_pratiche_count || 0);
                      const sentCount = Number(c.excluded_pratiche_count || 0);
                      const disabledSend = sendableCount <= 0;
                      return (
                        <Fragment key={`c-group-${c.customer_id}`}>
                          <TableRow hover>
                            <TableCell padding="checkbox">
                              <Checkbox checked={checked} onChange={() => toggleComunicazioneSelected(c.customer_id)} disabled={disabledSend} />
                            </TableCell>
                            <TableCell sx={{ width: 56 }}>
                              <IconButton size="small" onClick={() => toggleComunicazioneExpanded(c.customer_id)}>
                                {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {c.customer_display_name || getCustomerDisplayName(c)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {c.customer_email || '-'} {c.customer_telephone ? `| ${c.customer_telephone}` : ''}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {sendableCount.toLocaleString('it-IT')}
                                </Typography>
                                {comunicazioneShowSent && sentCount > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Già inviate: {sentCount.toLocaleString('it-IT')}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {formatMoney(c.totale_importo_transazione || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {c.last_inviato_il ? formatDate(c.last_inviato_il) : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={disabledSend ? 'Nessuna pratica da comunicare' : 'Invia comunicazione'}>
                                <span>
                                  <IconButton size="small" onClick={() => openEmailDialog(c)} disabled={disabledSend}>
                                    <Mail size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? '1px solid' : 'none', borderColor: 'divider' }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Pratica</TableCell>
                                        <TableCell>Creditore</TableCell>
                                        <TableCell>Tipologia</TableCell>
                                        <TableCell>Data chiusura</TableCell>
                                        {comunicazioneShowSent && <TableCell>Stato</TableCell>}
                                        <TableCell align="right">% Trans.</TableCell>
                                        <TableCell align="right">Importo trans.</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(c.pratiche || []).map((p) => (
                                        <TableRow key={`p-${c.customer_id}-${p.id}`} hover>
                                          <TableCell sx={{ fontWeight: 700 }}>
                                            {p.id}
                                          </TableCell>
                                          <TableCell>{p.issuer_name || '-'}</TableCell>
                                          <TableCell>{p.category_name || '-'}</TableCell>
                                          <TableCell>{formatDate(p.data_chiusura)}</TableCell>
                                          {comunicazioneShowSent && (
                                            <TableCell>
                                              {p.sendable ? (
                                                <Chip
                                                  size="small"
                                                  label="Inviabile"
                                                  sx={(theme) => ({
                                                    height: 22,
                                                    borderRadius: 999,
                                                    fontWeight: 800,
                                                    bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                                    color: theme.palette.success.main,
                                                    border: '1px solid',
                                                    borderColor: alpha(theme.palette.success.main, 0.45)
                                                  })}
                                                />
                                              ) : (
                                                <Chip
                                                  size="small"
                                                  label={getComunicazioneExcludedLabel(p.excluded_reason)}
                                                  sx={(theme) => ({
                                                    height: 22,
                                                    borderRadius: 999,
                                                    fontWeight: 800,
                                                    bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                                    color: theme.palette.warning.main,
                                                    border: '1px solid',
                                                    borderColor: alpha(theme.palette.warning.main, 0.45)
                                                  })}
                                                />
                                              )}
                                            </TableCell>
                                          )}
                                          <TableCell align="right">{formatPercent(p.perc_transazione || 0)}</TableCell>
                                          <TableCell align="right">{formatMoney(p.importo_transazione_autorizzata || 0)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={comunicazioneTotal}
              page={comunicazionePage}
              onPageChange={handleComunicazioneChangePage}
              rowsPerPage={comunicazioneRowsPerPage}
              onRowsPerPageChange={handleComunicazioneChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        ) : viewTab === 'sollecito' ? (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2, flexWrap: 'nowrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                  Solleciti (Transazioni)
                </Typography>
                <Tooltip
                  title={`Disponibile se: comunicazione inviata, piano rateale non inviato, ultimo sollecito oltre ${Number(sollecitoMinDaysBetween || 7)} giorni.`}
                >
                  <span>
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      <HelpCircle size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Button
                  variant={sollecitoShowSollecitati ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    clearSollecitoSelection();
                    setSollecitoExpanded({});
                    setSollecitoPage(0);
                    setSollecitoShowSollecitati((prev) => {
                      const next = !prev;
                      if (next) setSollecitoShowExcluded(false);
                      return next;
                    });
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    ...(sollecitoShowSollecitati
                      ? { bgcolor: 'rgb(33, 43, 54)', '&:hover': { bgcolor: 'rgb(22, 28, 36)' } }
                      : {})
                  }}
                >
                  {sollecitoShowSollecitati ? 'Sollecitati: ON' : 'Mostra sollecitati'}
                </Button>
                <Button
                  variant={sollecitoShowExcluded ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    clearSollecitoSelection();
                    setSollecitoExpanded({});
                    setSollecitoPage(0);
                    setSollecitoShowExcluded((prev) => {
                      const next = !prev;
                      if (next) setSollecitoShowSollecitati(false);
                      return next;
                    });
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    ...(sollecitoShowExcluded
                      ? { bgcolor: 'rgb(33, 43, 54)', '&:hover': { bgcolor: 'rgb(22, 28, 36)' } }
                      : {})
                  }}
                >
                  {sollecitoShowExcluded ? 'Esclusi: ON' : 'Mostra esclusi'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => setSollecitoSelectAllAnchorEl(e.currentTarget)}
                  startIcon={<ViewColumnIcon fontSize="small" />}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  Seleziona
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={openSollecitoBulkDialogForAllFiltered}
                  disabled={sollecitoLoading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 900,
                    bgcolor: 'rgb(33, 43, 54)',
                    '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
                  }}
                >
                  Invia tutti
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={openSollecitoBulkDialog}
                  disabled={!selectedSollecitoIds.length || sollecitoLoading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 900,
                    bgcolor: 'rgb(33, 43, 54)',
                    '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
                  }}
                >
                  Invia selezionati ({selectedSollecitoIds.length})
                </Button>
              </Box>
            </Box>

            <Menu
              anchorEl={sollecitoSelectAllAnchorEl}
              open={Boolean(sollecitoSelectAllAnchorEl)}
              onClose={() => setSollecitoSelectAllAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  setSollecitoSelectedForPage(true);
                  setSollecitoSelectAllAnchorEl(null);
                }}
              >
                Seleziona pagina
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSollecitoSelectedForPage(false);
                  setSollecitoSelectAllAnchorEl(null);
                }}
              >
                Deseleziona pagina
              </MenuItem>
              <MenuItem onClick={selectAllSollecitoFiltered}>Seleziona tutti filtrati</MenuItem>
              <MenuItem
                onClick={() => {
                  clearSollecitoSelection();
                  setSollecitoSelectAllAnchorEl(null);
                }}
              >
                Pulisci selezione
              </MenuItem>
            </Menu>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={sollecitoSelectionState.all}
                        indeterminate={sollecitoSelectionState.some}
                        onChange={(e) => setSollecitoSelectedForPage(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 56 }} />
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Pratiche</TableCell>
                    <TableCell align="right">Totale importo</TableCell>
                    <TableCell>Ultimo sollecito</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sollecitoLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Box sx={{ position: 'sticky', left: 0, width: '100vw', display: 'flex', justifyContent: 'center' }}>
                          <LoaderProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : visibleSollecitoRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                        <Box sx={{ position: 'sticky', left: 0, width: '100vw', display: 'flex', justifyContent: 'center' }}>
                          {sollecitoShowSollecitati
                            ? 'Nessun sollecito già inviato con i filtri selezionati'
                            : 'Nessun sollecito disponibile con i filtri selezionati'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleSollecitoRows.map((c) => {
                      const expanded = Boolean(sollecitoExpanded[String(c.customer_id)]);
                      const checked = Boolean(sollecitoSelected?.[String(c.customer_id)]);
                      const sendableCount = Number(c.pending_pratiche_count || 0);
                      const displayCount = sollecitoShowSollecitati ? Number((c.pratiche || []).length) : sendableCount;
                      const excludedCount = Number(c.excluded_pratiche_count || 0);
                      const disabledSend = sendableCount <= 0;
                      const disabledTooltip = disabledSend ? getSollecitoCustomerDisabledTooltip(c) : '';
                      const hasRecentSollecito =
                        disabledSend &&
                        Array.isArray(c?.pratiche) &&
                        c.pratiche.some((p) => p && p.sendable === false && String(p.excluded_reason || '').trim() === 'sollecito_recent');
                      return (
                        <Fragment key={`s-group-${c.customer_id}`}>
                          <TableRow hover>
                            <TableCell padding="checkbox">
                              <Checkbox checked={checked} onChange={() => toggleSollecitoSelected(c.customer_id)} disabled={disabledSend} />
                            </TableCell>
                            <TableCell sx={{ width: 56 }}>
                              <IconButton size="small" onClick={() => toggleSollecitoExpanded(c.customer_id)}>
                                {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {c.customer_display_name || getCustomerDisplayName(c)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {c.customer_email || '-'} {c.customer_telephone ? `| ${c.customer_telephone}` : ''}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {displayCount.toLocaleString('it-IT')}
                                </Typography>
                                {sollecitoShowExcluded && excludedCount > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Escluse: {excludedCount.toLocaleString('it-IT')}
                                  </Typography>
                                )}
                                {hasRecentSollecito && (
                                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 800 }}>
                                    {getSollecitoExcludedLabel('sollecito_recent')}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {formatMoney(c.totale_importo_transazione || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {c.last_inviato_il ? formatDate(c.last_inviato_il) : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={disabledSend ? disabledTooltip : 'Invia sollecito'}>
                                <span>
                                  <IconButton size="small" onClick={() => openSollecitoEmailDialog(c)} disabled={disabledSend}>
                                    <Mail size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? '1px solid' : 'none', borderColor: 'divider' }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Pratica</TableCell>
                                        <TableCell>Creditore</TableCell>
                                        <TableCell>Tipologia</TableCell>
                                        <TableCell>Data chiusura</TableCell>
                                        <TableCell>Ult. sollecito</TableCell>
                                        <TableCell>Stato</TableCell>
                                        <TableCell align="right">Importo trans.</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(c.pratiche || []).map((p) => (
                                        <TableRow key={`sp-${c.customer_id}-${p.id}`} hover>
                                          <TableCell sx={{ fontWeight: 700 }}>{p.id}</TableCell>
                                          <TableCell>{p.issuer_name || '-'}</TableCell>
                                          <TableCell>{p.category_name || '-'}</TableCell>
                                          <TableCell>{formatDate(p.data_chiusura)}</TableCell>
                                          <TableCell>{p.last_sollecito_il ? formatDate(p.last_sollecito_il) : '-'}</TableCell>
                                          <TableCell>
                                            {p.sendable ? (
                                              <Chip
                                                size="small"
                                                label="Inviabile"
                                                sx={(theme) => ({
                                                  height: 22,
                                                  borderRadius: 999,
                                                  fontWeight: 800,
                                                  bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                                  color: theme.palette.success.main,
                                                  border: '1px solid',
                                                  borderColor: alpha(theme.palette.success.main, 0.45)
                                                })}
                                              />
                                            ) : (
                                              <Chip
                                                size="small"
                                                label={getSollecitoExcludedLabel(p.excluded_reason)}
                                                sx={(theme) => ({
                                                  height: 22,
                                                  borderRadius: 999,
                                                  fontWeight: 800,
                                                  bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                                  color: theme.palette.warning.main,
                                                  border: '1px solid',
                                                  borderColor: alpha(theme.palette.warning.main, 0.45)
                                                })}
                                              />
                                            )}
                                          </TableCell>
                                          <TableCell align="right">{formatMoney(p.importo_transazione_autorizzata || 0)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={sollecitoTotal}
              page={sollecitoPage}
              onPageChange={handleSollecitoChangePage}
              rowsPerPage={sollecitoRowsPerPage}
              onRowsPerPageChange={handleSollecitoChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        ) : viewTab === 'piano_rateale' ? (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2, flexWrap: 'nowrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                  Piano rateale (Transazioni)
                </Typography>
                <Tooltip title="Disponibile se: comunicazione inviata e piano rateale non ancora inviato.">
                  <span>
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      <HelpCircle size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Button
                  variant={pianoRatealeShowSent ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    clearPianoRatealeSelection();
                    setPianoRatealeExpanded({});
                    setPianoRatealePage(0);
                    setPianoRatealeShowSent((prev) => !prev);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    ...(pianoRatealeShowSent ? { bgcolor: 'rgb(33, 43, 54)', '&:hover': { bgcolor: 'rgb(22, 28, 36)' } } : {})
                  }}
                >
                  {pianoRatealeShowSent ? 'Già inviate: ON' : 'Mostra già inviate'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => setPianoRatealeSelectAllAnchorEl(e.currentTarget)}
                  startIcon={<ViewColumnIcon fontSize="small" />}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  Seleziona
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={openPianoRatealeBulkDialogForAllFiltered}
                  disabled={pianoRatealeLoading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 900,
                    bgcolor: 'rgb(33, 43, 54)',
                    '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
                  }}
                >
                  Invia tutti
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={openPianoRatealeBulkDialog}
                  disabled={!selectedPianoRatealeIds.length || pianoRatealeLoading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 900,
                    bgcolor: 'rgb(33, 43, 54)',
                    '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
                  }}
                >
                  Invia selezionati ({selectedPianoRatealeIds.length})
                </Button>
              </Box>
            </Box>

            <Menu
              anchorEl={pianoRatealeSelectAllAnchorEl}
              open={Boolean(pianoRatealeSelectAllAnchorEl)}
              onClose={() => setPianoRatealeSelectAllAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  setPianoRatealeSelectedForPage(true);
                  setPianoRatealeSelectAllAnchorEl(null);
                }}
              >
                Seleziona pagina
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setPianoRatealeSelectedForPage(false);
                  setPianoRatealeSelectAllAnchorEl(null);
                }}
              >
                Deseleziona pagina
              </MenuItem>
              <MenuItem onClick={selectAllPianoRatealeFiltered}>Seleziona tutti filtrati</MenuItem>
              <MenuItem
                onClick={() => {
                  clearPianoRatealeSelection();
                  setPianoRatealeSelectAllAnchorEl(null);
                }}
              >
                Pulisci selezione
              </MenuItem>
            </Menu>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={pianoRatealeSelectionState.all}
                        indeterminate={pianoRatealeSelectionState.some}
                        onChange={(e) => setPianoRatealeSelectedForPage(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 56 }} />
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Pratiche</TableCell>
                    <TableCell align="right">Totale importo</TableCell>
                    <TableCell>Ultimo invio</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pianoRatealeLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Box sx={{ position: 'sticky', left: 0, width: '100vw', display: 'flex', justifyContent: 'center' }}>
                          <LoaderProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : visiblePianoRatealeRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                        <Box sx={{ position: 'sticky', left: 0, width: '100vw', display: 'flex', justifyContent: 'center' }}>
                          {pianoRatealeShowSent
                            ? 'Nessun piano rateale disponibile con i filtri selezionati'
                            : 'Nessun piano rateale da inviare con i filtri selezionati'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visiblePianoRatealeRows.map((c) => {
                      const expanded = Boolean(pianoRatealeExpanded[String(c.customer_id)]);
                      const checked = Boolean(pianoRatealeSelected?.[String(c.customer_id)]);
                      const sendableCount = Number(c.pending_pratiche_count || 0);
                      const excludedCount = Number(c.excluded_pratiche_count || 0);
                      const disabledSend = sendableCount <= 0;
                      return (
                        <Fragment key={`pr-group-${c.customer_id}`}>
                          <TableRow hover>
                            <TableCell padding="checkbox">
                              <Checkbox checked={checked} onChange={() => togglePianoRatealeSelected(c.customer_id)} disabled={disabledSend} />
                            </TableCell>
                            <TableCell sx={{ width: 56 }}>
                              <IconButton size="small" onClick={() => togglePianoRatealeExpanded(c.customer_id)}>
                                {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {c.customer_display_name || getCustomerDisplayName(c)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {c.customer_email || '-'} {c.customer_telephone ? `| ${c.customer_telephone}` : ''}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {sendableCount.toLocaleString('it-IT')}
                                </Typography>
                                {pianoRatealeShowSent && excludedCount > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    Già inviate: {excludedCount.toLocaleString('it-IT')}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {formatMoney(c.totale_importo_transazione || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {c.last_inviato_il ? formatDate(c.last_inviato_il) : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                {c.last_inviato_il && (
                                  <Tooltip title="Vedi piano concordato">
                                    <IconButton size="small" onClick={() => openPianoRatealeViewDialog(c)}>
                                      <Eye size={18} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title={disabledSend ? 'Nessuna pratica da pianificare' : 'Invia piano rateale'}>
                                  <span>
                                    <IconButton size="small" onClick={() => openPianoRatealeEmailDialog(c)} disabled={disabledSend}>
                                      <Mail size={18} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? '1px solid' : 'none', borderColor: 'divider' }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Pratica</TableCell>
                                        <TableCell>Creditore</TableCell>
                                        <TableCell>Tipologia</TableCell>
                                        <TableCell>Data chiusura</TableCell>
                                        {pianoRatealeShowSent && <TableCell>Stato</TableCell>}
                                        <TableCell align="right">Importo trans.</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(c.pratiche || []).map((p) => (
                                        <TableRow key={`pr-${c.customer_id}-${p.id}`} hover>
                                          <TableCell sx={{ fontWeight: 700 }}>{p.id}</TableCell>
                                          <TableCell>{p.issuer_name || '-'}</TableCell>
                                          <TableCell>{p.category_name || '-'}</TableCell>
                                          <TableCell>{formatDate(p.data_chiusura)}</TableCell>
                                          {pianoRatealeShowSent && (
                                            <TableCell>
                                              {p.sendable ? (
                                                <Chip
                                                  size="small"
                                                  label="Inviabile"
                                                  sx={(theme) => ({
                                                    height: 22,
                                                    borderRadius: 999,
                                                    fontWeight: 800,
                                                    bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                                    color: theme.palette.success.main,
                                                    border: '1px solid',
                                                    borderColor: alpha(theme.palette.success.main, 0.45)
                                                  })}
                                                />
                                              ) : (
                                                <Chip
                                                  size="small"
                                                  label={getPianoRatealeExcludedLabel(p.excluded_reason)}
                                                  sx={(theme) => ({
                                                    height: 22,
                                                    borderRadius: 999,
                                                    fontWeight: 800,
                                                    bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
                                                    color: theme.palette.warning.main,
                                                    border: '1px solid',
                                                    borderColor: alpha(theme.palette.warning.main, 0.45)
                                                  })}
                                                />
                                              )}
                                            </TableCell>
                                          )}
                                          <TableCell align="right">{formatMoney(p.importo_transazione_autorizzata || 0)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={pianoRatealeTotal}
              page={pianoRatealePage}
              onPageChange={handlePianoRatealeChangePage}
              rowsPerPage={pianoRatealeRowsPerPage}
              onRowsPerPageChange={handlePianoRatealeChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Tab in preparazione.
            </Typography>
          </Box>
        )}
      </StyledCard>

      <Dialog
        open={emailDialogOpen}
        onClose={(_, reason) => {
          if (emailDialogLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          closeEmailDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, pt: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Mail size={18} color="#0284c7" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  Email (Comunicazione transazione)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Cliente: {emailTarget?.customer_display_name || (emailTarget ? getCustomerDisplayName(emailTarget) : '-')}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={closeEmailDialog} disabled={emailDialogLoading} sx={{ color: 'text.secondary' }}>
              <X size={16} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {emailDialogLoading && !emailForm.subject ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <LoaderProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={emailAccounts}
                  loading={emailAccountsLoading}
                  value={emailAccounts.find((a) => String(a?.id) === String(emailForm.email_account_id)) || null}
                  onChange={(_e, next) => setEmailForm((prev) => ({ ...prev, email_account_id: next?.id ?? null }))}
                  getOptionLabel={(o) => `${o?.name || ''} ${o?.email ? `<${o.email}>` : ''}`.trim()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Account mittente *"
                      fullWidth
                      InputProps={{
                        ...params.InputProps
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="To *"
                  fullWidth
                  value={emailForm.to}
                  onChange={(e) => setEmailForm((prev) => ({ ...prev, to: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Cc"
                  fullWidth
                  value={emailForm.cc}
                  onChange={(e) => setEmailForm((prev) => ({ ...prev, cc: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Bcc"
                  fullWidth
                  value={emailForm.bcc}
                  onChange={(e) => setEmailForm((prev) => ({ ...prev, bcc: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Oggetto *"
                  fullWidth
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm((prev) => ({ ...prev, subject: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                  Contenuto email *
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    overflowX: 'auto',
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : '#fff'),
                    '& .ql-toolbar': {
                      border: 'none',
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    },
                    '& .ql-container': {
                      border: 'none',
                      minHeight: 90
                    }
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    formats={quillFormats}
                    value={emailForm.body_intro_html}
                    onChange={(value) => setEmailForm((prev) => ({ ...prev, body_intro_html: value }))}
                  />

                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 1.5, py: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                        Tabella pratiche
                      </Typography>
                      <Tooltip title="Seleziona colonne">
                        <IconButton size="small" onClick={(e) => setEmailColumnsAnchorEl(e.currentTarget)} sx={{ color: 'text.secondary' }}>
                          <ViewColumnIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Menu
                      anchorEl={emailColumnsAnchorEl}
                      open={Boolean(emailColumnsAnchorEl)}
                      onClose={() => setEmailColumnsAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <Box sx={{ px: 2, pt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Colonne visibili
                        </Typography>
                      </Box>
                      <FormGroup sx={{ px: 2, pb: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.issuer_name}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, issuer_name: !prev.issuer_name }))}
                            />
                          }
                          label="Creditore"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.category_name}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, category_name: !prev.category_name }))}
                            />
                          }
                          label="Tipologia"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.calcolo_ipotesi}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, calcolo_ipotesi: !prev.calcolo_ipotesi }))}
                            />
                          }
                          label="Calcolo"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.data_chiusura}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, data_chiusura: !prev.data_chiusura }))}
                            />
                          }
                          label="Data chiusura"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.importo_chiusura}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, importo_chiusura: !prev.importo_chiusura }))}
                            />
                          }
                          label="Importo chiusura"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.importo_accordato}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, importo_accordato: !prev.importo_accordato }))}
                            />
                          }
                          label="Importo accordato"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.risparmio}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, risparmio: !prev.risparmio }))}
                            />
                          }
                          label="Risparmio"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.base_compenso}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, base_compenso: !prev.base_compenso }))}
                            />
                          }
                          label="Base Compenso"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.perc_transazione}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, perc_transazione: !prev.perc_transazione }))}
                            />
                          }
                          label="% Trans."
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={emailColumnVisibility.importo_transazione}
                              onChange={() => setEmailColumnVisibility((prev) => ({ ...prev, importo_transazione: !prev.importo_transazione }))}
                            />
                          }
                          label="Importo trans."
                        />
                      </FormGroup>
                    </Menu>

                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell sx={{ fontWeight: 800 }}>Pratica</TableCell>
                          {emailColumnVisibility.issuer_name && <TableCell sx={{ fontWeight: 800 }}>Creditore</TableCell>}
                          {emailColumnVisibility.category_name && <TableCell sx={{ fontWeight: 800 }}>Tipologia</TableCell>}
                          {emailColumnVisibility.calcolo_ipotesi && <TableCell sx={{ fontWeight: 800 }}>Calcolo</TableCell>}
                          {emailColumnVisibility.data_chiusura && <TableCell sx={{ fontWeight: 800 }}>Data chiusura</TableCell>}
                          {emailColumnVisibility.importo_chiusura && (
                            <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                              Importo chiusura
                            </TableCell>
                          )}
                          {emailColumnVisibility.importo_accordato && (
                            <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                              Importo accordato
                            </TableCell>
                          )}
                          {emailColumnVisibility.risparmio && (
                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                              Risparmio
                            </TableCell>
                          )}
                          {emailColumnVisibility.base_compenso && (
                            <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                              Base Compenso
                            </TableCell>
                          )}
                          {emailColumnVisibility.perc_transazione && (
                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                              % Trans.
                            </TableCell>
                          )}
                          {emailColumnVisibility.importo_transazione && (
                            <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                              Importo trans.
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(emailForm.table_rows || []).map((r) => (
                          <Fragment key={`email-row-${r.id}`}>
                            <TableRow hover>
                              <TableCell padding="checkbox">
                                <Checkbox checked={Boolean(r.include)} onChange={() => toggleEmailRowInclude(r.id)} />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <IconButton size="small" onClick={() => toggleEmailDettagli(r.id)} sx={{ p: 0.25, color: 'text.secondary' }}>
                                    {emailDettagliExpanded?.[r.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                  </IconButton>
                                  {r.id}
                                </Box>
                              </TableCell>
                            {emailColumnVisibility.issuer_name && (
                              <TableCell sx={{ minWidth: 180 }}>
                                <TextField
                                  variant="standard"
                                  value={r.issuer_name || ''}
                                  onChange={(e) => updateEmailRowField(r.id, 'issuer_name', e.target.value)}
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            {emailColumnVisibility.category_name && (
                              <TableCell sx={{ minWidth: 160 }}>
                                <TextField
                                  variant="standard"
                                  value={r.category_name || ''}
                                  onChange={(e) => updateEmailRowField(r.id, 'category_name', e.target.value)}
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            {emailColumnVisibility.calcolo_ipotesi && (
                              <TableCell sx={{ minWidth: 170 }}>
                                <TextField
                                  variant="standard"
                                  value={getCalcoloIpotesiLabel(normalizeCalcoloIpotesiKey(r.calcolo_ipotesi)) || ''}
                                  disabled
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            {emailColumnVisibility.data_chiusura && (
                              <TableCell sx={{ minWidth: 120 }}>
                                <TextField
                                  variant="standard"
                                  value={r.data_chiusura || ''}
                                  onChange={(e) => updateEmailRowField(r.id, 'data_chiusura', e.target.value)}
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            {emailColumnVisibility.importo_chiusura && (
                              <TableCell align="right" sx={{ minWidth: 140 }}>
                                <TextField
                                  variant="standard"
                                  value={r.importo_chiusura || ''}
                                  onChange={(e) => updateEmailRowField(r.id, 'importo_chiusura', e.target.value)}
                                  inputProps={{ style: { textAlign: 'right' } }}
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            {emailColumnVisibility.importo_accordato && (
                              <TableCell align="right" sx={{ minWidth: 140 }}>
                                <TextField
                                  variant="standard"
                                  value={r.importo_accordato || ''}
                                  onChange={(e) => updateEmailRowField(r.id, 'importo_accordato', e.target.value)}
                                  inputProps={{ style: { textAlign: 'right' } }}
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            {emailColumnVisibility.risparmio && (
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                <TextField
                                  variant="standard"
                                  value={r.risparmio || ''}
                                  onChange={(e) => updateEmailRowField(r.id, 'risparmio', e.target.value)}
                                  inputProps={{ style: { textAlign: 'right' } }}
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            {emailColumnVisibility.base_compenso && (
                              <TableCell align="right" sx={{ minWidth: 140 }}>
                                <TextField
                                  variant="standard"
                                  value={r.base_compenso || ''}
                                  disabled
                                  inputProps={{ style: { textAlign: 'right' } }}
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            {emailColumnVisibility.perc_transazione && (
                              <TableCell align="right" sx={{ minWidth: 90 }}>
                                <TextField
                                  variant="standard"
                                  value={r.perc_transazione || ''}
                                  onChange={(e) => updateEmailRowField(r.id, 'perc_transazione', e.target.value)}
                                  inputProps={{ style: { textAlign: 'right' } }}
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            {emailColumnVisibility.importo_transazione && (
                              <TableCell align="right" sx={{ minWidth: 140 }}>
                                <TextField
                                  variant="standard"
                                  value={r.importo_transazione || ''}
                                  onChange={(e) => updateEmailRowField(r.id, 'importo_transazione', e.target.value)}
                                  inputProps={{ style: { textAlign: 'right' } }}
                                  fullWidth
                                />
                              </TableCell>
                            )}
                            </TableRow>
                            {emailDettagliExpanded?.[r.id] && (
                              <TableRow>
                                <TableCell colSpan={emailTableColSpan} sx={{ bgcolor: '#fcfcfd' }}>
                                  <Box sx={{ px: 0.5, py: 0.75 }}>{renderEmailCalcoloDettagli(r)}</Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        ))}
                        <TableRow>
                          <TableCell
                            colSpan={
                              2 +
                              (emailColumnVisibility.issuer_name ? 1 : 0) +
                              (emailColumnVisibility.category_name ? 1 : 0) +
                              (emailColumnVisibility.calcolo_ipotesi ? 1 : 0) +
                              (emailColumnVisibility.data_chiusura ? 1 : 0)
                            }
                            sx={{ fontWeight: 900, bgcolor: '#f9fafb' }}
                          >
                            Totale
                          </TableCell>
                          {emailColumnVisibility.importo_chiusura && (
                            <TableCell align="right" sx={{ fontWeight: 900, bgcolor: '#f9fafb' }}>
                              {formatNumber2It(
                                (emailForm.table_rows || [])
                                  .filter((x) => x?.include)
                                  .reduce((sum, x) => sum + Number(parseItNumber(x.importo_chiusura) || 0), 0)
                              )}
                            </TableCell>
                          )}
                          {emailColumnVisibility.importo_accordato && (
                            <TableCell align="right" sx={{ fontWeight: 900, bgcolor: '#f9fafb' }}>
                              {formatNumber2It(
                                (emailForm.table_rows || [])
                                  .filter((x) => x?.include)
                                  .reduce((sum, x) => sum + Number(parseItNumber(x.importo_accordato) || 0), 0)
                              )}
                            </TableCell>
                          )}
                          {emailColumnVisibility.risparmio && (
                            <TableCell align="right" sx={{ fontWeight: 900, bgcolor: '#f9fafb' }}>
                              {formatNumber2It(
                                (emailForm.table_rows || [])
                                  .filter((x) => x?.include)
                                  .reduce((sum, x) => sum + Number(parseItNumber(x.risparmio) || 0), 0)
                              )}
                            </TableCell>
                          )}
                          {emailColumnVisibility.base_compenso && (
                            <TableCell align="right" sx={{ fontWeight: 900, bgcolor: '#f9fafb' }}>
                              {formatNumber2It(
                                (emailForm.table_rows || [])
                                  .filter((x) => x?.include)
                                  .reduce((sum, x) => sum + Number(parseItNumber(x.base_compenso) || 0), 0)
                              )}
                            </TableCell>
                          )}
                          {emailColumnVisibility.perc_transazione && <TableCell sx={{ bgcolor: '#f9fafb' }} />}
                          {emailColumnVisibility.importo_transazione && (
                            <TableCell align="right" sx={{ fontWeight: 900, bgcolor: '#f9fafb' }}>
                              {formatNumber2It(
                                (emailForm.table_rows || [])
                                  .filter((x) => x?.include)
                                  .reduce((sum, x) => sum + Number(parseItNumber(x.importo_transazione) || 0), 0)
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>

                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    <ReactQuill
                      theme="snow"
                      modules={{ toolbar: false }}
                      formats={quillFormats}
                      value={emailForm.body_footer_html}
                      onChange={(value) => setEmailForm((prev) => ({ ...prev, body_footer_html: value }))}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={closeEmailDialog} disabled={emailDialogLoading} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={emailDialogLoading}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {emailDialogLoading ? 'Invio...' : 'Invia'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sollecitoEmailDialogOpen}
        onClose={(_, reason) => {
          if (sollecitoEmailDialogLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          closeSollecitoEmailDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Mail size={18} color="#0284c7" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  Email (Sollecito accordo)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Cliente: {sollecitoEmailTarget?.customer_display_name || (sollecitoEmailTarget ? getCustomerDisplayName(sollecitoEmailTarget) : '-')}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={closeSollecitoEmailDialog}
              disabled={sollecitoEmailDialogLoading}
              sx={{ color: 'text.secondary' }}
            >
              <X size={16} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {sollecitoEmailDialogLoading && !sollecitoEmailForm.subject ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <LoaderProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={emailAccounts}
                  loading={emailAccountsLoading}
                  value={emailAccounts.find((a) => String(a?.id) === String(sollecitoEmailForm.email_account_id)) || null}
                  onChange={(_e, next) => setSollecitoEmailForm((prev) => ({ ...prev, email_account_id: next?.id ?? null }))}
                  getOptionLabel={(o) => `${o?.name || ''} ${o?.email ? `<${o.email}>` : ''}`.trim()}
                  renderInput={(params) => <TextField {...params} label="Account mittente *" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="To *"
                  fullWidth
                  value={sollecitoEmailForm.to}
                  onChange={(e) => setSollecitoEmailForm((prev) => ({ ...prev, to: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Cc"
                  fullWidth
                  value={sollecitoEmailForm.cc}
                  onChange={(e) => setSollecitoEmailForm((prev) => ({ ...prev, cc: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Bcc"
                  fullWidth
                  value={sollecitoEmailForm.bcc}
                  onChange={(e) => setSollecitoEmailForm((prev) => ({ ...prev, bcc: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Oggetto *"
                  fullWidth
                  value={sollecitoEmailForm.subject}
                  onChange={(e) => setSollecitoEmailForm((prev) => ({ ...prev, subject: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                  Contenuto email *
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    overflowX: 'auto',
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : '#fff'),
                    '& .ql-toolbar': {
                      border: 'none',
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    },
                    '& .ql-container': {
                      border: 'none',
                      minHeight: 180
                    }
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    formats={quillFormats}
                    value={sollecitoEmailForm.body_html}
                    onChange={(value) => setSollecitoEmailForm((prev) => ({ ...prev, body_html: value }))}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={closeSollecitoEmailDialog} disabled={sollecitoEmailDialogLoading} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleSendSollecitoEmail}
            disabled={sollecitoEmailDialogLoading}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {sollecitoEmailDialogLoading ? 'Invio...' : 'Invia'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pianoRatealeEmailDialogOpen}
        onClose={(_, reason) => {
          if (pianoRatealeEmailDialogLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          closePianoRatealeEmailDialog();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Mail size={18} color="#0284c7" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  Email (Piano rateale)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Cliente:{' '}
                  {pianoRatealeEmailTarget?.customer_display_name ||
                    (pianoRatealeEmailTarget ? getCustomerDisplayName(pianoRatealeEmailTarget) : '-')}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={closePianoRatealeEmailDialog}
              disabled={pianoRatealeEmailDialogLoading}
              sx={{ color: 'text.secondary' }}
            >
              <X size={16} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {pianoRatealeEmailDialogLoading && !pianoRatealeEmailForm.subject ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <LoaderProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={emailAccounts}
                  loading={emailAccountsLoading}
                  value={emailAccounts.find((a) => String(a?.id) === String(pianoRatealeEmailForm.email_account_id)) || null}
                  onChange={(_e, next) => setPianoRatealeEmailForm((prev) => ({ ...prev, email_account_id: next?.id ?? null }))}
                  getOptionLabel={(o) => `${o?.name || ''} ${o?.email ? `<${o.email}>` : ''}`.trim()}
                  renderInput={(params) => <TextField {...params} label="Account mittente *" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="To *"
                  fullWidth
                  value={pianoRatealeEmailForm.to}
                  onChange={(e) => setPianoRatealeEmailForm((prev) => ({ ...prev, to: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Cc"
                  fullWidth
                  value={pianoRatealeEmailForm.cc}
                  onChange={(e) => setPianoRatealeEmailForm((prev) => ({ ...prev, cc: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Bcc"
                  fullWidth
                  value={pianoRatealeEmailForm.bcc}
                  onChange={(e) => setPianoRatealeEmailForm((prev) => ({ ...prev, bcc: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Oggetto *"
                  fullWidth
                  value={pianoRatealeEmailForm.subject}
                  onChange={(e) => setPianoRatealeEmailForm((prev) => ({ ...prev, subject: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Importo totale (EUR)"
                  fullWidth
                  value={pianoRatealeEmailForm.rate_plan?.total_amount || ''}
                  onChange={(e) =>
                    setPianoRatealeEmailForm((prev) => ({ ...prev, rate_plan: { ...(prev.rate_plan || {}), total_amount: e.target.value } }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Numero rate"
                  fullWidth
                  value={pianoRatealeEmailForm.rate_plan?.rate_count ?? 10}
                  onChange={(e) =>
                    setPianoRatealeEmailForm((prev) => ({
                      ...prev,
                      rate_plan: { ...(prev.rate_plan || {}), rate_count: Number(e.target.value || 0) || 0 }
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Prima rata (gg/mm/aaaa)"
                  fullWidth
                  value={pianoRatealeEmailForm.rate_plan?.first_due_date || ''}
                  onChange={(e) =>
                    setPianoRatealeEmailForm((prev) => ({
                      ...prev,
                      rate_plan: { ...(prev.rate_plan || {}), first_due_date: e.target.value }
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={regeneratePianoRatealeRows}
                  disabled={pianoRatealeEmailDialogLoading}
                  sx={{ textTransform: 'none', fontWeight: 900, bgcolor: 'rgb(33, 43, 54)', '&:hover': { bgcolor: 'rgb(22, 28, 36)' } }}
                >
                  Rigenera rate
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                  Contenuto email
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    overflowX: 'auto',
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : '#fff'),
                    '& .ql-toolbar': {
                      border: 'none',
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    },
                    '& .ql-container': {
                      border: 'none',
                      minHeight: 120
                    }
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    formats={quillFormats}
                    value={pianoRatealeEmailForm.body_intro_html}
                    onChange={(value) => setPianoRatealeEmailForm((prev) => ({ ...prev, body_intro_html: value }))}
                  />

                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 72, fontWeight: 800 }}>N.</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Scadenza</TableCell>
                          <TableCell align="right" sx={{ width: 180, fontWeight: 800 }}>
                            Importo
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(pianoRatealeEmailForm.rate_rows || []).map((r) => (
                          <TableRow key={`rate-${r.idx}`} hover>
                            <TableCell sx={{ fontWeight: 800 }}>{r.idx}</TableCell>
                            <TableCell>
                              <TextField
                                variant="standard"
                                fullWidth
                                value={r.due_date || ''}
                                onChange={(e) => updatePianoRatealeRowField(r.idx, 'due_date', e.target.value)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                variant="standard"
                                fullWidth
                                value={r.amount || ''}
                                onChange={(e) => updatePianoRatealeRowField(r.idx, 'amount', e.target.value)}
                                inputProps={{ style: { textAlign: 'right' } }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={2} sx={{ fontWeight: 800 }}>
                            Totale
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900 }}>
                            {formatNumber2It(
                              (pianoRatealeEmailForm.rate_rows || []).reduce((sum, x) => sum + Number(parseItNumber(x?.amount) || 0), 0)
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>

                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    <ReactQuill
                      theme="snow"
                      modules={{ toolbar: false }}
                      formats={quillFormats}
                      value={pianoRatealeEmailForm.body_footer_html}
                      onChange={(value) => setPianoRatealeEmailForm((prev) => ({ ...prev, body_footer_html: value }))}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={closePianoRatealeEmailDialog} disabled={pianoRatealeEmailDialogLoading} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleSendPianoRatealeEmail}
            disabled={pianoRatealeEmailDialogLoading}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {pianoRatealeEmailDialogLoading ? 'Invio...' : 'Invia'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pianoRatealeViewDialogOpen}
        onClose={(_, reason) => {
          if (pianoRatealeViewDialogLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          closePianoRatealeViewDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Eye size={18} color="#334155" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  Piano concordato
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Cliente:{' '}
                  {pianoRatealeViewData?.customer?.customer_display_name ||
                    (pianoRatealeViewData?.customer ? getCustomerDisplayName(pianoRatealeViewData.customer) : '-')}
                  {pianoRatealeViewData?.inviato_il ? ` • Inviato il ${formatDate(pianoRatealeViewData.inviato_il)}` : ''}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={closePianoRatealeViewDialog} disabled={pianoRatealeViewDialogLoading} sx={{ color: 'text.secondary' }}>
              <X size={16} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {pianoRatealeViewDialogLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <LoaderProgress />
            </Box>
          ) : !pianoRatealeViewData ? (
            <Box sx={{ py: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                Nessun dato disponibile
              </Typography>
            </Box>
          ) : (
            (() => {
              const plan = pianoRatealeViewData?.rate_plan || {};
              const rows = Array.isArray(pianoRatealeViewData?.rate_rows) ? pianoRatealeViewData.rate_rows : [];
              const sorted = rows.slice().sort((a, b) => Number(a?.idx || 0) - Number(b?.idx || 0));
              const total = sorted.reduce((sum, r) => sum + Number(parseItNumber(r?.amount) || 0), 0);
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Importo totale
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {formatNumber2It(parseItNumber(plan?.total_amount), String(plan?.total_amount || '')) || formatNumber2It(total)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Numero rate
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {Number(plan?.rate_count || sorted.length || 0) || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Prima rata
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {String(plan?.first_due_date || sorted?.[0]?.due_date || '-') || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Frequenza
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {String(plan?.frequency || 'mensile') || 'mensile'}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 80 }}>Rata</TableCell>
                          <TableCell sx={{ width: 140 }}>Scadenza</TableCell>
                          <TableCell align="right">Importo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sorted.map((r) => (
                          <TableRow key={`view-rate-${r.idx}`}>
                            <TableCell sx={{ fontWeight: 800 }}>{r.idx}</TableCell>
                            <TableCell>{r.due_date || '-'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                              {formatNumber2It(parseItNumber(r.amount), r.amount || '')}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={2} sx={{ fontWeight: 900 }}>
                            Totale
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900 }}>
                            {formatNumber2It(total)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              );
            })()
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={closePianoRatealeViewDialog} disabled={pianoRatealeViewDialogLoading} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pianoRatealeBulkDialogOpen}
        onClose={(_, reason) => {
          if (pianoRatealeBulkSending) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setPianoRatealeBulkDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Mail size={18} color="#0284c7" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  Invio massivo (Piano rateale)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Clienti: {selectedPianoRatealeIds.length}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setPianoRatealeBulkDialogOpen(false)}
              disabled={pianoRatealeBulkSending}
              sx={{ color: 'text.secondary' }}
            >
              <X size={16} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={emailAccounts}
                loading={emailAccountsLoading}
                value={emailAccounts.find((a) => String(a?.id) === String(pianoRatealeBulkAccountId)) || null}
                onChange={(_e, next) => setPianoRatealeBulkAccountId(next?.id ?? null)}
                getOptionLabel={(o) => `${o?.name || ''} ${o?.email ? `<${o.email}>` : ''}`.trim()}
                renderInput={(params) => <TextField {...params} label="Account mittente *" fullWidth />}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Avanzamento
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                  {pianoRatealeBulkProgress.done}/{pianoRatealeBulkProgress.total}
                </Typography>
              </Box>
              {pianoRatealeBulkSending && (
                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                  <LoaderProgress />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => setPianoRatealeBulkDialogOpen(false)} disabled={pianoRatealeBulkSending} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handlePianoRatealeBulkSend}
            disabled={pianoRatealeBulkSending}
            sx={{ textTransform: 'none', fontWeight: 900, bgcolor: 'rgb(33, 43, 54)', '&:hover': { bgcolor: 'rgb(22, 28, 36)' } }}
          >
            {pianoRatealeBulkSending ? 'Invio...' : 'Avvia invio'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sollecitoBulkDialogOpen}
        onClose={(_, reason) => {
          if (sollecitoBulkSending) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setSollecitoBulkDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Mail size={18} color="#0284c7" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  Invio massivo (Sollecito)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Clienti: {selectedSollecitoIds.length}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setSollecitoBulkDialogOpen(false)}
              disabled={sollecitoBulkSending}
              sx={{ color: 'text.secondary' }}
            >
              <X size={16} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={emailAccounts}
                loading={emailAccountsLoading}
                value={emailAccounts.find((a) => String(a?.id) === String(sollecitoBulkAccountId)) || null}
                onChange={(_e, next) => setSollecitoBulkAccountId(next?.id ?? null)}
                getOptionLabel={(o) => `${o?.name || ''} ${o?.email ? `<${o.email}>` : ''}`.trim()}
                renderInput={(params) => <TextField {...params} label="Account mittente *" fullWidth />}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Avanzamento
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                  {sollecitoBulkProgress.done}/{sollecitoBulkProgress.total}
                </Typography>
              </Box>
              {sollecitoBulkSending && (
                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                  <LoaderProgress />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => setSollecitoBulkDialogOpen(false)} disabled={sollecitoBulkSending} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleSollecitoBulkSend}
            disabled={sollecitoBulkSending}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              bgcolor: 'rgb(33, 43, 54)',
              '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
            }}
          >
            {sollecitoBulkSending ? 'Invio...' : 'Invia'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={comunicazioneBulkDialogOpen}
        onClose={(_, reason) => {
          if (comunicazioneBulkSending) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setComunicazioneBulkDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Mail size={18} color="#0284c7" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  Invio massivo (Comunicazione)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Clienti: {selectedComunicazioneIds.length}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setComunicazioneBulkDialogOpen(false)}
              disabled={comunicazioneBulkSending}
              sx={{ color: 'text.secondary' }}
            >
              <X size={16} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={emailAccounts}
                loading={emailAccountsLoading}
                value={emailAccounts.find((a) => String(a?.id) === String(comunicazioneBulkAccountId)) || null}
                onChange={(_e, next) => setComunicazioneBulkAccountId(next?.id ?? null)}
                getOptionLabel={(o) => `${o?.name || ''} ${o?.email ? `<${o.email}>` : ''}`.trim()}
                renderInput={(params) => <TextField {...params} label="Account mittente *" fullWidth />}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Avanzamento
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                  {comunicazioneBulkProgress.done}/{comunicazioneBulkProgress.total}
                </Typography>
              </Box>
              {comunicazioneBulkSending && (
                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                  <LoaderProgress />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={() => setComunicazioneBulkDialogOpen(false)}
            disabled={comunicazioneBulkSending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleComunicazioneBulkSend}
            disabled={comunicazioneBulkSending}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              bgcolor: 'rgb(33, 43, 54)',
              '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
            }}
          >
            {comunicazioneBulkSending ? 'Invio...' : 'Invia'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={calcoloIpotesiChangeDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setCalcoloIpotesiChangeDialogOpen(false);
          setPendingCalcoloIpotesi(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AlertTriangle size={20} color="#f59e0b" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                Cambiare tipologia di calcolo?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
                Da: {getCalcoloIpotesiLabel(calcoloIpotesi)} → A: {getCalcoloIpotesiLabel(pendingCalcoloIpotesi)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Cambiando tipologia, i dati del calcolo verranno resettati ai valori di default recuperati dalla pratica.
          </Typography>
          {calcoloIpotesiSaved && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 700 }}>
              Calcolo salvato: {getCalcoloIpotesiLabel(calcoloIpotesiSaved)}
            </Typography>
          )}
          {calcoloDirty && (
            <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 900 }}>
              Attenzione: ci sono modifiche non salvate.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={() => {
              setCalcoloIpotesiChangeDialogOpen(false);
              setPendingCalcoloIpotesi(null);
            }}
            disabled={verifyLoading}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const next = pendingCalcoloIpotesi;
              setCalcoloIpotesiChangeDialogOpen(false);
              setPendingCalcoloIpotesi(null);
              applyCalcoloIpotesiChange(next);
            }}
            disabled={verifyLoading || !pendingCalcoloIpotesi}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              bgcolor: 'rgb(33, 43, 54)',
              '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
            }}
          >
            Cambia calcolo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={verifyDialogOpen}
        onClose={(_, reason) => {
          if (verifyLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          closeVerifyDialog();
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AlertTriangle size={20} color="#f59e0b" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  Conferma (cambio stato)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                  Cliente: {verifyRow ? getCustomerDisplayName(verifyRow) : '-'} | Tel: {verifyRow?.customer_telephone || '-'} | Email: {verifyRow?.customer_email || '-'} |
                  Legale: {verifyRow?.responsabile_legale_name || '-'} | Creditore: {verifyRow?.issuer_name || '-'} | Tipologia: {verifyRow?.category_name || '-'} | Chiusura:{' '}
                  {verifyRow?.modalita_chiusura || '-'}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={closeVerifyDialog}
              disabled={verifyLoading}
              sx={{ color: 'text.secondary' }}
            >
              <X size={16} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
              gap: 2,
              alignItems: 'start'
            }}
          >
            <Paper variant="outlined" sx={{ borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                Dati di partenza
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Data Chiusura *"
                    type="date"
                    fullWidth
                    value={verifyForm.data_chiusura}
                    onChange={(e) => setVerifyForm((prev) => ({ ...prev, data_chiusura: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Importo Chiusura (€) *"
                    type="number"
                    fullWidth
                    value={verifyForm.importo_chiusura}
                    onChange={(e) => {
                      const value = e.target.value;
                      setImportoTransazioneAuto(true);
                      setVerifyForm((prev) => ({ ...prev, importo_chiusura: value }));
                      if (calcoloIpotesi === 'saldo_stralcio') setCalcoloInput('importo_dovuto', value);
                      if (calcoloIpotesi === 'carta_revolving') setCalcoloInput('saldo_debito', value);
                      if (calcoloIpotesi === 'finanziamento_decaduto') setCalcoloInput('richiesto_a_decadenza', value);
                      if (calcoloIpotesi === 'piano_rientro') setCalcoloInput('totale_da_restituire', value);
                    }}
                    onBlur={() => setVerifyForm((prev) => ({ ...prev, importo_chiusura: formatNumber2(prev.importo_chiusura, '0.00') }))}
                    inputProps={{ step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Importo Accordato (€)"
                    type="number"
                    fullWidth
                    value={verifyForm.importo_accordato}
                    onChange={(e) => {
                      const value = e.target.value;
                      setImportoTransazioneAuto(true);
                      setVerifyForm((prev) => ({ ...prev, importo_accordato: value }));
                      if (calcoloIpotesi === 'saldo_stralcio') setCalcoloInput('importo_accordato', value);
                      if (calcoloIpotesi === 'finanziamento_decaduto') setCalcoloInput('importo_accordato', value);
                    }}
                    onBlur={() => setVerifyForm((prev) => ({ ...prev, importo_accordato: formatNumber2(prev.importo_accordato, '0.00') }))}
                    inputProps={{ step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Importo Interessi (€) *"
                    type="number"
                    fullWidth
                    value={verifyForm.importo_interessi}
                    onChange={(e) => setVerifyForm((prev) => ({ ...prev, importo_interessi: e.target.value }))}
                    onBlur={() => setVerifyForm((prev) => ({ ...prev, importo_interessi: formatNumber2(prev.importo_interessi, '0.00') }))}
                    inputProps={{ step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Base Compenso (€) *"
                    type="number"
                    fullWidth
                    value={formatNumber2(baseCompensoForVerify, '0.00')}
                    disabled
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={getCalcoloIpotesiHelp(calcoloIpotesi)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', cursor: 'help' }}>
                              <HelpCircle size={16} />
                            </Box>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                    inputProps={{ step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Percentuale Transazione (%) *"
                    type="number"
                    fullWidth
                    value={verifyForm.perc_transazione}
                    onChange={(e) => {
                      const value = e.target.value;
                      setImportoTransazioneAuto(true);
                      setVerifyForm((prev) => ({ ...prev, perc_transazione: value }));
                    }}
                    onBlur={() => setVerifyForm((prev) => ({ ...prev, perc_transazione: formatNumber2(prev.perc_transazione, '0.00') }))}
                    inputProps={{ step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Importo Transazione (€) *"
                    type="number"
                    fullWidth
                    value={verifyForm.importo_transazione}
                    onChange={(e) => {
                      const value = e.target.value;
                      setImportoTransazioneAuto(false);
                      setVerifyForm((prev) => {
                        const next = { ...prev, importo_transazione: value };
                        const base = Number(baseCompensoForVerify || 0);
                        const newPerc = base > 0 ? (Number(value || 0) / base) * 100 : Number(prev.perc_transazione || 0);
                        if (Number.isFinite(newPerc)) next.perc_transazione = formatNumber2(newPerc, next.perc_transazione);
                        return next;
                      });
                    }}
                    onBlur={() => setVerifyForm((prev) => ({ ...prev, importo_transazione: formatNumber2(prev.importo_transazione, '0.00') }))}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={getCalcoloIpotesiHelp(calcoloIpotesi)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', cursor: 'help' }}>
                              <HelpCircle size={16} />
                            </Box>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                    inputProps={{ step: '0.01' }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                Calcolo importo transazione
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                <Tabs
                  value={calcoloIpotesi}
                  onChange={(_e, next) => {
                    if (next === calcoloIpotesi) return;
                    if (calcoloDirty || Boolean(calcoloIpotesiSaved)) {
                      setPendingCalcoloIpotesi(next);
                      setCalcoloIpotesiChangeDialogOpen(true);
                      return;
                    }
                    applyCalcoloIpotesiChange(next);
                  }}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                >
                  {CALCOLO_IPOTESI.map((o) => (
                    <Tab
                      key={o.key}
                      value={o.key}
                      label={
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                            {o.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                            Base: {o.baseLabel}
                          </Typography>
                        </Box>
                      }
                      sx={{ textTransform: 'none' }}
                    />
                  ))}
                </Tabs>
              </Paper>

              <Box sx={{ mt: 1.5 }}>
                <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : '#f8fafc')
                    }}
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        Dati da inserire
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        Calcoli automatici
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    {calcoloIpotesi === 'piano_rientro' && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 220px', columnGap: 2, rowGap: 1.25, alignContent: 'start' }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Importo erogato
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Inserito (contratto)
                            </Typography>
                          </Box>
                          <Box>
                            <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.importo_erogato || ''} onChange={(e) => setCalcoloInput('importo_erogato', e.target.value)} inputProps={{ step: '0.01' }} />
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Totale da restituire
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Inserito (piano di ammortamento)
                            </Typography>
                          </Box>
                          <Box>
                            <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.totale_da_restituire || ''} onChange={(e) => setCalcoloInput('totale_da_restituire', e.target.value)} inputProps={{ step: '0.01' }} />
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              N. rate del piano
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Inserito
                            </Typography>
                          </Box>
                          <Box>
                            <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.numero_rate || ''} onChange={(e) => setCalcoloInput('numero_rate', e.target.value)} inputProps={{ step: '1' }} />
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              N. rate pagate
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Inserito (quietanze)
                            </Typography>
                          </Box>
                          <Box>
                            <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.rate_pagate || ''} onChange={(e) => setCalcoloInput('rate_pagate', e.target.value)} inputProps={{ step: '1' }} />
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Data prima rata
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Inserito
                            </Typography>
                          </Box>
                          <Box>
                            <TextField size="small" type="date" fullWidth value={currentCalcoloInputs.data_prima_rata || ''} onChange={(e) => setCalcoloInput('data_prima_rata', e.target.value)} InputLabelProps={{ shrink: true }} />
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Ultima rata pagata
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Inserito
                            </Typography>
                          </Box>
                          <Box>
                            <TextField size="small" type="date" fullWidth value={currentCalcoloInputs.data_ultima_pagata || ''} onChange={(e) => setCalcoloInput('data_ultima_pagata', e.target.value)} InputLabelProps={{ shrink: true }} />
                          </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 220px', columnGap: 2, rowGap: 1.25, alignContent: 'start' }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Rata
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Automatico: totale ÷ n. rate
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                            {formatMoney(calcoloComputed.outputs?.rata)}
                          </Typography>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Interessi totali
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Automatico: totale − erogato
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                            {formatMoney(calcoloComputed.outputs?.interessi_totali)}
                          </Typography>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Interessi per rata
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Automatico: interessi ÷ n. rate
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                            {formatMoney(calcoloComputed.outputs?.interessi_per_rata)}
                          </Typography>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Interessi già pagati
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Automatico: interessi per rata × rate pagate
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                            {formatMoney(calcoloComputed.outputs?.interessi_gia_pagati)}
                          </Typography>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Rate residue
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Automatico: n. rate − rate pagate
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                            {Number(calcoloComputed.outputs?.rate_residue || 0)}
                          </Typography>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Debito residuo
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Automatico: totale − rata × rate pagate
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                            {formatMoney(calcoloComputed.outputs?.debito_residuo)}
                          </Typography>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                              Interessi risparmiati
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              Automatico: interessi totali − interessi pagati
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                            {formatMoney(calcoloComputed.outputs?.interessi_risparmiati)}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {calcoloIpotesi === 'saldo_stralcio' && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 220px', columnGap: 2, rowGap: 1.25, alignContent: 'start' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Importo dovuto
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (lettera del creditore)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={currentCalcoloInputs.importo_dovuto || ''}
                            onChange={(e) => {
                              setCalcoloInput('importo_dovuto', e.target.value);
                              setImportoTransazioneAuto(true);
                            }}
                            inputProps={{ step: '0.01' }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Fonte dell’importo
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (scelta)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" fullWidth value={currentCalcoloInputs.fonte_importo || ''} onChange={(e) => setCalcoloInput('fonte_importo', e.target.value)} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Importo accordato
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (accordo)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={currentCalcoloInputs.importo_accordato || ''}
                            onChange={(e) => {
                              setCalcoloInput('importo_accordato', e.target.value);
                              setImportoTransazioneAuto(true);
                            }}
                            inputProps={{ step: '0.01' }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Modalità di pagamento
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (scelta)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" fullWidth value={currentCalcoloInputs.modalita_pagamento || ''} onChange={(e) => setCalcoloInput('modalita_pagamento', e.target.value)} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            N. rate dell’accordo
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.numero_rate || ''} onChange={(e) => setCalcoloInput('numero_rate', e.target.value)} inputProps={{ step: '1' }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Prima scadenza
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="date" fullWidth value={currentCalcoloInputs.prima_scadenza || ''} onChange={(e) => setCalcoloInput('prima_scadenza', e.target.value)} InputLabelProps={{ shrink: true }} />
                        </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 220px', columnGap: 2, rowGap: 1.25, alignContent: 'start' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Sconto
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: (dovuto − accordato) ÷ dovuto
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatPercentIt(calcoloComputed.outputs?.sconto_percent || 0, 2)}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Rata dell’accordo
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: accordato ÷ n. rate
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatMoney(calcoloComputed.outputs?.rata)}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>
                            Risparmio
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: dovuto − accordato
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatMoney(calcoloComputed.outputs?.risparmio)}
                        </Typography>
                        </Box>
                      </Box>
                    )}

                    {calcoloIpotesi === 'carta_revolving' && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 220px', columnGap: 2, rowGap: 1.25, alignContent: 'start' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Tipo di carta
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (scelta)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" fullWidth value={currentCalcoloInputs.tipo_carta || ''} onChange={(e) => setCalcoloInput('tipo_carta', e.target.value)} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Saldo a debito
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (estratto conto)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={currentCalcoloInputs.saldo_debito || ''}
                            onChange={(e) => {
                              setCalcoloInput('saldo_debito', e.target.value);
                              setImportoTransazioneAuto(true);
                            }}
                            inputProps={{ step: '0.01' }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Interessi da estratto conto
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (estratto conto)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={currentCalcoloInputs.interessi_estratto_conto || ''}
                            onChange={(e) => {
                              setCalcoloInput('interessi_estratto_conto', e.target.value);
                              setImportoTransazioneAuto(true);
                            }}
                            inputProps={{ step: '0.01' }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Periodo (da)
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="date" fullWidth value={currentCalcoloInputs.periodo_da || ''} onChange={(e) => setCalcoloInput('periodo_da', e.target.value)} InputLabelProps={{ shrink: true }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Periodo (a)
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="date" fullWidth value={currentCalcoloInputs.periodo_a || ''} onChange={(e) => setCalcoloInput('periodo_a', e.target.value)} InputLabelProps={{ shrink: true }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            TAEG
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (contratto o estratto conto)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.taeg || ''} onChange={(e) => setCalcoloInput('taeg', e.target.value)} inputProps={{ step: '0.01' }} />
                        </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 220px', columnGap: 2, rowGap: 1.25, alignContent: 'start' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Capitale al netto degli interessi
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: saldo − interessi
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatMoney(calcoloComputed.outputs?.capitale_netto_interessi)}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Incidenza degli interessi
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: interessi ÷ saldo
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatPercentIt(calcoloComputed.outputs?.incidenza_interessi_percent || 0, 2)}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>
                            Risparmio
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: uguale agli interessi da E/C
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatMoney(calcoloComputed.outputs?.risparmio)}
                        </Typography>
                        </Box>
                      </Box>
                    )}

                    {calcoloIpotesi === 'finanziamento_decaduto' && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 220px', columnGap: 2, rowGap: 1.25, alignContent: 'start' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Importo erogato
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (contratto)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.importo_erogato || ''} onChange={(e) => setCalcoloInput('importo_erogato', e.target.value)} inputProps={{ step: '0.01' }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Totale da restituire
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (piano di ammortamento)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.totale_da_restituire || ''} onChange={(e) => setCalcoloInput('totale_da_restituire', e.target.value)} inputProps={{ step: '0.01' }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            N. rate del piano
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.numero_rate || ''} onChange={(e) => setCalcoloInput('numero_rate', e.target.value)} inputProps={{ step: '1' }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Importo già pagato
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (quietanze)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.importo_pagato || ''} onChange={(e) => setCalcoloInput('importo_pagato', e.target.value)} inputProps={{ step: '0.01' }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Importo scaduto
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (lettera di decadenza)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="number" fullWidth value={currentCalcoloInputs.importo_scaduto || ''} onChange={(e) => setCalcoloInput('importo_scaduto', e.target.value)} inputProps={{ step: '0.01' }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Data decadenza
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito
                          </Typography>
                        </Box>
                        <Box>
                          <TextField size="small" type="date" fullWidth value={currentCalcoloInputs.data_decadenza || ''} onChange={(e) => setCalcoloInput('data_decadenza', e.target.value)} InputLabelProps={{ shrink: true }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Richiesto a decadenza
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (intimazione)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={currentCalcoloInputs.richiesto_a_decadenza || ''}
                            onChange={(e) => {
                              setCalcoloInput('richiesto_a_decadenza', e.target.value);
                              setImportoTransazioneAuto(true);
                            }}
                            inputProps={{ step: '0.01' }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Importo accordato
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Inserito (accordo)
                          </Typography>
                        </Box>
                        <Box>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={currentCalcoloInputs.importo_accordato || ''}
                            onChange={(e) => {
                              setCalcoloInput('importo_accordato', e.target.value);
                              setImportoTransazioneAuto(true);
                            }}
                            inputProps={{ step: '0.01' }}
                          />
                        </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 220px', columnGap: 2, rowGap: 1.25, alignContent: 'start' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Rata
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: totale ÷ n. rate
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatMoney(calcoloComputed.outputs?.rata)}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Rate pagate
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: importo pagato ÷ rata
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatNumber2It(calcoloComputed.outputs?.rate_pagate, '-')}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Rate scadute non pagate
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: importo scaduto ÷ rata
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatNumber2It(calcoloComputed.outputs?.rate_scadute_non_pagate, '-')}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Debito residuo da piano
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: totale − importo pagato
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatMoney(calcoloComputed.outputs?.debito_residuo_piano)}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Interessi delle rate future
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatMoney(calcoloComputed.outputs?.interessi_rate_future)}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>
                            Risparmio
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                            Automatico: richiesto − accordato
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 900, textAlign: 'right', alignSelf: 'center' }}>
                          {formatMoney(calcoloComputed.outputs?.risparmio)}
                        </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
              </Paper>
            </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={verifyRow?.stato_movimento === 'verificato' ? handleUnverifyConfirm : closeVerifyDialog}
            disabled={verifyLoading}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {verifyRow?.stato_movimento === 'verificato' ? 'Annulla transazione' : 'Annulla'}
          </Button>
          <Button
            variant="contained"
            onClick={handleVerifyConfirm}
            disabled={verifyLoading}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {verifyRow?.stato_movimento === 'verificato'
              ? verifyLoading
                ? 'Salvataggio...'
                : 'Conferma'
              : verifyLoading
                ? 'Verifica...'
                : 'Conferma e Verifica'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unverifyForceDialogOpen}
        onClose={(_, reason) => {
          if (verifyLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setUnverifyForceDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AlertTriangle size={18} color="#dc2626" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  Forza annullamento?
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Pratica: {verifyRow?.id || '-'}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setUnverifyForceDialogOpen(false)} disabled={verifyLoading} sx={{ color: 'text.secondary' }}>
              <X size={16} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>
            {unverifyBlockInfo?.message ||
              'Esistono invii collegati (Comunicazione, Sollecito e/o Piano rateale). Se procedi con la forza cancellazione verranno eliminati fisicamente.'}
          </Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fase</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Inviato il</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(unverifyBlockInfo?.dependencies?.invii || []).map((r) => (
                  <TableRow key={`dep-${r.invio_id}`}>
                    <TableCell sx={{ fontWeight: 800 }}>{String(r.fase || '').replace('_', ' ')}</TableCell>
                    <TableCell>{r.stato || '-'}</TableCell>
                    <TableCell>{r.inviato_il ? formatDate(r.inviato_il) : '-'}</TableCell>
                  </TableRow>
                ))}
                {!(unverifyBlockInfo?.dependencies?.invii || []).length && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nessun dettaglio disponibile
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => setUnverifyForceDialogOpen(false)} disabled={verifyLoading} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Chiudi
          </Button>
          {!unverifyBlockInfo?.isBlocking && (
            <Button
              variant="outlined"
              onClick={() => attemptUnverify(false)}
              disabled={verifyLoading}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              {verifyLoading ? 'Eliminazione...' : 'Annulla solo calcolo'}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => attemptUnverify(true)}
            disabled={verifyLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              bgcolor: 'rgb(33, 43, 54)',
              '&:hover': { bgcolor: 'rgb(22, 28, 36)' }
            }}
          >
            {verifyLoading ? 'Eliminazione...' : 'Forza annullamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
